import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf'; // eslint-disable-line no-unused-vars

// Features
import Login from "./features/auth/Login";
import Register from "./features/auth/Register";
import SuperAdminDashboard from "./features/admin/SuperAdminDashboard";
import SettingsPage from "./features/settings/SettingsPage";
import DashboardHome from "./features/dashboard/DashboardHome";
import PublicSignedView from "./features/dashboard/PublicSignedView";
import { generateConsentPDF } from './common/utils/pdfGenerator';
import { toast } from './common/utils/toast';

// Common
import { QRCodeModal } from './common/components/QRCodeModal';
import { formatDateForInput } from './common/utils/formatters';
import { isOwnerOrAdmin, filterClientsByRole, filterFormsByRole, isPageRestricted } from './common/utils/roleHelpers';

import {
    LayoutDashboard, Send, Users, UserCog, FileText,
    Settings, Activity, Mail, Search, LogOut, Megaphone,
    Plus, ChevronRight, X, Edit, FilePlus, Save, Trash2, CheckCircle, RefreshCw, CheckSquare, Check,
    Shield, Lock, Smartphone, Monitor, ChevronDown, Eye, Menu,
    Instagram, Facebook, Twitter, Camera,
    Calendar,
    Type, Image as ImageIcon,
    User, Home, MapPin, PenTool, QrCode, AlertCircle, ArrowLeft,
    Building2, Maximize2, ChevronLeft, Palette, MousePointer, MessageSquare
} from 'lucide-react';

// Configure Axios to send cookies with EVERY request
axios.defaults.withCredentials = true;

// ─── Toast Notification System ───────────────────────────────────────────────
function ToastContainer() {
    const [toasts, setToasts] = useState([]);
    useEffect(() => {
        toast._register((type, message) => {
            const id = Date.now() + Math.random();
            setToasts(prev => [...prev, { id, type, message }]);
            setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
        });
        return () => toast._unregister();
    }, []);
    if (!toasts.length) return null;
    return (
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none max-w-sm w-full">
            {toasts.map(t => (
                <div key={t.id} className={`flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-sm font-medium pointer-events-auto ${
                    t.type === 'success' ? 'bg-green-600' : t.type === 'error' ? 'bg-red-600' : 'bg-slate-700'
                }`}>
                    <span className="flex-shrink-0 mt-0.5">
                        {t.type === 'success' ? <Check size={15} /> : t.type === 'error' ? <X size={15} /> : <AlertCircle size={15} />}
                    </span>
                    <span className="leading-snug">{t.message}</span>
                </div>
            ))}
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────────────────

// 🌐 Dynamic API URL Resolution
const getApiUrl = () => {
    // If we have an ENV variable, use it
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

    // Otherwise fallback to current host /api
    const host = window.location.origin;
    return `${host}/api`;
};

const API_URL = getApiUrl();

// Helper for image compression
const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new window.Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', quality));
            };
        };
    });
};

// ----------------------------------------------------------------------
// MAIN APP COMPONENT
// ----------------------------------------------------------------------
export default function App() {
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem('bookink_user');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                                return null;
            }
        }
        return null;
    });
    const [view, setView] = useState('login');
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [publicFormData, setPublicFormData] = useState(null);
    const [publicClient, setPublicClient] = useState(null);
    const hasLoggedOut = React.useRef(false); // Debounce flag for 401 interceptor

    // CENTRAL DATA STATE
    const [clients, setClients] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [consentForms, setConsentForms] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [allStudios, setAllStudios] = useState([]);
    const [selectedStudioId, setSelectedStudioId] = useState(() => {
        const saved = localStorage.getItem('bookink_user');
        if (saved) {
            try {
                const u = JSON.parse(saved);
                return u.studio_id || u.id;
            } catch (e) { return null; }
        }
        return null;
    });

    // Initial load: User persistence + URL Form Parameter
    useEffect(() => {
        // Restore JWT token from localStorage for API authentication
        const savedToken = localStorage.getItem('bookink_token');
        if (savedToken) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        }

        // Ref to track if we are in the initial load phase for interceptors
        let isFirstLoadFinished = false;

        // Initial session check
        axios.get(`${API_URL}/auth/me`)
            .then(res => {
                if (res.data.success) {
                    setUser(res.data.user);
                    if (res.data.user.role !== 'superadmin') {
                        setSelectedStudioId(res.data.user.studio_id || res.data.user.id);
                    } else {
                        setSelectedStudioId(null); // Super admin sees all by default
                    }
                    localStorage.setItem('bookink_user', JSON.stringify(res.data.user)); // Keep localStorage updated
                }
            })
            .catch(err => {
                // If 401, we are not logged in, which is fine on mount
                if (err.response?.status === 401) {
                    // Don't clear user state if on a public signing route
                    const path = window.location.pathname;
                    if (!path.startsWith('/sign/')) {
                        localStorage.removeItem('bookink_user');
                        setUser(null);
                    }
                }
            })
            .finally(() => {
                isFirstLoadFinished = true;
                // Determine if we should wait for more data (public form/client)
                const params = new URLSearchParams(window.location.search);
                if (!params.get('formId') && !params.get('campaign_id') && !params.get('company') && !params.get('view_client_id') && !params.get('token') && !window.location.pathname.startsWith('/sign/') && !(params.get('studio_id') && params.get('prefill_client_id'))) {
                    setIsInitialLoading(false);
                }
            });

        // Add Global Interceptor for 401s
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    // Don't alert or force logout if it's just the initial auth check failing for a guest
                    if (error.config && error.config.url && error.config.url.endsWith('/auth/me')) {
                        return Promise.reject(error);
                    }
                    
                    // Don't intercept if we're already trying to log out
                    if (error.config && error.config.url && error.config.url.endsWith('/auth/logout')) {
                        return Promise.reject(error);
                    }

                    // NEW: Don't alert if we are on a public signing page or campaign resolving
                    const params = new URLSearchParams(window.location.search);
                    const isPublicPath = params.get('formId') || params.get('token') || params.get('campaign_id') || params.get('company') || params.get('view_client_id') || (params.get('studio_id') && params.get('prefill_client_id')) || window.location.pathname.startsWith('/sign/');
                    if (isPublicPath) {
                        return Promise.reject(error);
                    }

                    // Debounce: only fire once to prevent infinite alert loop
                    if (!hasLoggedOut.current) {
                        hasLoggedOut.current = true;
                        
                        // If this is during the initial load, just log out silently without an alert.
                        // The user had a stale session in localStorage, so we clean it up quietly.
                        if (!isFirstLoadFinished) {
                            console.warn("🔐 401 Unauthorized detected during initial load - clearing stale session silently");
                            localStorage.removeItem('bookink_user');
                            setUser(null);
                            handleLogout();
                        } else {
                            console.warn("🔐 401 Unauthorized detected - triggering handleLogout (once)");
                            // Prevent alert from blocking the UI loop if multiple requests fail
                            setTimeout(() => {
                                toast.error("Your session has expired. Please login again.");
                                handleLogout();
                            }, 100);
                        }
                    }
                }
                return Promise.reject(error);
            }
        );

        // Handle QR Code / Public Link: ?formId=XXX or /sign/TOKEN
        const params = new URLSearchParams(window.location.search);
        const path = window.location.pathname;
        const isSignRoute = path.startsWith('/sign/');
        const token = isSignRoute ? path.split('/')[2] : params.get('token');
        const formId = params.get('formId');
        const campaignId = params.get('campaign_id');
        const companyName = params.get('company');
        const prefillClientId = params.get('prefill_client_id');
        const kioskMode = params.get('mode') === 'kiosk'; // Check for kiosk mode

        const loadPublicData = async () => {
            try {
                if (token) {
                    try {
                        const res = await axios.get(`${API_URL}/forms/token/${token}`);
                        // The endpoint returns { form, customer, studio, artists, consentConfig }
                        // We need to map this to publicFormData for the PublicSigningPage
                        const effectiveStudioId = (res.data.form.studio_id && res.data.form.studio_id !== 1)
                            ? res.data.form.studio_id
                            : res.data.studio.id;

                        setPublicFormData({
                            isTokenWorkflow: true,
                            ...res.data.consentConfig, // title, description, agreements, health_conditions
                            id: res.data.consentFormId,
                            token: res.data.form.token,
                            status: res.data.form.status,
                            studio_id: effectiveStudioId,
                            isKiosk: false,
                            prefillData: {
                                first_name: res.data.customer.first_name,
                                last_name: res.data.customer.last_name,
                                email: res.data.customer.email,
                                phone: res.data.customer.phone || '',
                                address: res.data.customer.address || '',
                                postcode: res.data.customer.postcode || '',
                                dob: res.data.customer.dob || ''
                            },
                            artists: res.data.artists,
                            studioDetails: res.data.studio
                        });
                    } catch (e) {
                                                setPublicFormData(null); // Or something to show error
                    }
                } else if (formId) {
                    const studioIdParam = params.get('studio_id');
                    const formQuery = studioIdParam ? `?studio_id=${encodeURIComponent(studioIdParam)}` : '';
                    const res = await axios.get(`${API_URL}/forms${formQuery}`);
                    // eslint-disable-next-line eqeqeq
                    const found = res.data.find(f => f.id == formId);
                    if (found) {
                        const effectiveStudioId = studioIdParam ? Number(studioIdParam) : found.studio_id;
                        const baseFormData = { ...found, studio_id: effectiveStudioId, isKiosk: kioskMode };
                        if (prefillClientId) {
                            try {
                                const clientRes = await axios.get(`${API_URL}/public/clients/${prefillClientId}`);
                                setPublicFormData({ ...baseFormData, prefillData: clientRes.data });
                            } catch (e) {
                                setPublicFormData(baseFormData);
                            }
                        } else {
                            setPublicFormData(baseFormData);
                        }
                    }
                } else if (campaignId || companyName) {
                    const query = campaignId ? `campaign_id=${campaignId}` : `company=${encodeURIComponent(companyName)}`;
                    const res = await axios.get(`${API_URL}/campaigns/resolve?${query}`);
                    const { form_id, studio_id } = res.data;

                    if (form_id) {
                        const formRes = await axios.get(`${API_URL}/forms?studio_id=${studio_id}`);
                        // eslint-disable-next-line eqeqeq
                        const found = formRes.data.find(f => f.id == form_id);
                        if (found) setPublicFormData({ ...found, studio_id: studio_id });
                    } else if (studio_id) {
                        const formRes = await axios.get(`${API_URL}/forms?studio_id=${studio_id}`);
                        const defaultForm = formRes.data.find(f => f.is_default === 1) || formRes.data[0];
                        if (defaultForm) setPublicFormData({ ...defaultForm, studio_id: studio_id });
                    }
                } else if (!formId && !campaignId && !companyName && !token && !params.get('view_client_id')) {
                    // studio_id-only or studio_id+prefill_client_id URL (email fallback links)
                    const studioIdParam = params.get('studio_id');
                    if (studioIdParam) {
                        const formRes = await axios.get(`${API_URL}/forms?studio_id=${encodeURIComponent(studioIdParam)}`);
                        const firstForm = formRes.data[0];
                        if (firstForm) {
                            const baseFormData = { ...firstForm, studio_id: Number(studioIdParam), isKiosk: kioskMode };
                            if (prefillClientId) {
                                try {
                                    const clientRes = await axios.get(`${API_URL}/public/clients/${prefillClientId}`);
                                    setPublicFormData({ ...baseFormData, prefillData: clientRes.data });
                                } catch (e) {
                                    setPublicFormData(baseFormData);
                                }
                            } else {
                                setPublicFormData(baseFormData);
                            }
                        }
                    }
                }
                const viewClientId = params.get('view_client_id');
                if (viewClientId && !token) {
                    try {
                        const res = await axios.get(`${API_URL}/clients/${viewClientId}`);
                        setPublicClient(res.data);
                    } catch (e) {
                                            }
                }
            } catch (err) {
                            } finally {
                setIsInitialLoading(false);
            }
        };

        loadPublicData();

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    const refreshData = useCallback(() => {
        if (!user) return;
        const studioId = (user.role === 'superadmin' && selectedStudioId) ? selectedStudioId : (user.studio_id || user.id);

        const filterByStudio = (data) => {
            if (!Array.isArray(data)) return [];
            // If super admin and no specific studio selected (viewing "all" or default), show everything
            if (user.role === 'superadmin' && (Number(studioId) === Number(user.id) || !selectedStudioId)) return data.sort((a, b) => b.id - a.id);
            return data.filter(item => Number(item.studio_id) === Number(studioId) || item.is_default === 1).sort((a, b) => b.id - a.id);
        };

        axios.get(`${API_URL}/clients`).then(res => setClients(filterByStudio(res.data))).catch(console.error);
        axios.get(`${API_URL}/staff`).then(res => setStaffList(filterByStudio(res.data))).catch(console.error);
        axios.get(`${API_URL}/campaigns`).then(res => setCampaigns(filterByStudio(res.data))).catch(console.error);
        axios.get(`${API_URL}/forms`).then(res => setConsentForms(filterByStudio(res.data))).catch(console.error);

        if (user.role === 'superadmin') {
            axios.get(`${API_URL}/studios`).then(res => setAllStudios(Array.isArray(res.data) ? res.data : [])).catch(console.error);
        }
    }, [user, selectedStudioId]);

    useEffect(() => {
        if (user) refreshData();
    }, [user, selectedStudioId, refreshData]);

    useEffect(() => {
        if (!user) return;
        const interval = setInterval(refreshData, 30000);
        return () => clearInterval(interval);
    }, [user, refreshData]);

    // URL Synchronization Logic
    useEffect(() => {
        const currentParams = new URLSearchParams(window.location.search);

        // If the current URL still has a raw token param, don't touch it — still loading
        if (currentParams.get('token')) return;

        if (publicFormData) {
            // Token workflow: use formId+studio URL (strip the raw token now that it's resolved)
            // Non-token workflow: same. Either way, push the clean canonical URL.
            const sid = publicFormData.studio_id;
            const targetUrl = sid
                ? `/?formId=${publicFormData.id}&studio_id=${sid}`
                : `/?formId=${publicFormData.id}`;
            // Only push if we're not already at the right URL
            if (!window.location.search.includes(`formId=${publicFormData.id}`)) {
                window.history.pushState({}, '', targetUrl);
            }
        } else if (publicClient) {
            const targetUrl = `/?view_client_id=${publicClient.id}`;
            if (window.location.search !== targetUrl) {
                window.history.pushState({}, '', targetUrl);
            }
        } else if (user) {
            // Logged in, no public form: clean up login/register paths only
            // Do NOT clear formId/token from URL — loadPublicData may still be running
            if (window.location.pathname === '/login' || window.location.pathname === '/register') {
                window.history.pushState({}, '', '/');
            }
        } else {
            // Not logged in, no form loaded yet: guard all known public URL patterns
            if (
                currentParams.get('view_client_id') ||
                currentParams.get('formId') ||
                currentParams.get('campaign_id') ||
                currentParams.get('company') ||
                currentParams.get('token') ||
                (currentParams.get('studio_id') && currentParams.get('prefill_client_id'))
            ) {
                return; // Still waiting for loadPublicData
            }
            const targetPath = `/${view}`;
            if (window.location.pathname !== targetPath) {
                window.history.pushState({}, '', targetPath);
            }
        }
    }, [user, view, publicFormData, publicClient]);

    const onLogin = (userData) => {
        hasLoggedOut.current = false; // Reset debounce flag on login
        // Ensure Axios headers are set with the *fresh* token 
        // because Login.js might be rendered in a different scope or we need it strictly set here.
        const token = localStorage.getItem('bookink_token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        setUser(userData);
        setView('dashboard');
        if (userData.role !== 'superadmin') {
            setSelectedStudioId(userData.studio_id || userData.id);
        } else {
            setSelectedStudioId(null);
        }
        localStorage.setItem('bookink_user', JSON.stringify(userData));
    };

    const handleLogout = () => {
        hasLoggedOut.current = false; // Reset the 401 debounce flag
        axios.post(`${API_URL}/auth/logout`).finally(() => {
            setUser(null);
            setSelectedStudioId(null);
            setClients([]);
            setStaffList([]);
            setConsentForms([]);
            setCampaigns([]);
            setAllStudios([]);
            setView('login');
            localStorage.removeItem('bookink_user');
            localStorage.removeItem('bookink_currentPage');
            localStorage.removeItem('bookink_token');
            delete axios.defaults.headers.common['Authorization'];
        });
    };

    // --- LOADING STATE ---
    if (isInitialLoading) {
        return (
            <div className="flex h-screen bg-slate-50 items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest text-xs">Loading BookInk...</p>
                </div>
            </div>
        );
    }

    // Show signing page for any visitor with a formId — logged in or not
    if (publicFormData) {
        return <PublicSigningPage form={publicFormData} ownerStudioId={user?.studio_id || user?.id} onBack={() => { setPublicFormData(null); refreshData(); }} />;
    }



    if (publicClient && !user) {
        return <PublicSignedView client={publicClient} studio={publicClient.studio || { studio_name: 'BookInk Studio' }} onBack={() => setPublicClient(null)} />;
    }

    if (user) {
        return (
            <>
                <Dashboard
                    user={user}
                    clients={clients}
                    staffList={staffList}
                    consentForms={consentForms}
                    campaigns={campaigns}
                    allStudios={allStudios}
                    selectedStudioId={selectedStudioId}
                    setSelectedStudioId={setSelectedStudioId}
                    onLogout={handleLogout}
                    onRefresh={refreshData}
                    onOpenPublicForm={(form) => setPublicFormData({
                        ...form,
                        studio_id: user?.role === 'superadmin'
                            ? (selectedStudioId || form.studio_id)
                            : (user?.studio_id || user?.id)
                    })}
                    setUser={setUser}
                />

                {/* IN-STUDIO SIGNING OVERLAY: Keeps Dashboard internal state alive */}
                {publicFormData && (
                    <div className="fixed inset-0 z-[1000] bg-white overflow-hidden pointer-events-auto shadow-2xl">
                        <PublicSigningPage form={publicFormData} ownerStudioId={user?.studio_id || user?.id} onBack={() => { setPublicFormData(null); refreshData(); }} />
                    </div>
                )}
            </>
        );
    }

    return view === 'login' ? <Login onLogin={onLogin} onSwitch={() => setView('register')} /> : <Register onSwitch={() => setView('login')} />;
}

// ----------------------------------------------------------------------
// PAGE: USER PROFILE (NEW) - Matches image_b5dedb.png
// ----------------------------------------------------------------------
const UserProfilePage = ({ user, onEdit }) => {
    const [activeTab, setActiveTab] = useState('account');

    // Security Form State
    // Security Form State - Initialize with user email
    const [securityForm, setSecurityForm] = useState({
        email: user.email || user.studio_email || '',
        password: '',
        confirmPassword: ''
    });

    const handleUpdateSecurity = async () => {
        if (securityForm.password !== securityForm.confirmPassword) {
            toast.error("Passwords do not match!"); return;
        }
        if (!securityForm.password) {
            toast.error("Please enter a new password."); return;
        }
        try {
            await axios.post(`${API_URL}/profile/update`, {
                ...securityForm,
                id: user.id,
                role: user.role,
                isStudio: user.isStudio || isOwnerOrAdmin(user)
            });
            toast.success("Security settings updated successfully!");
            setSecurityForm({ email: user.email || user.studio_email || '', password: '', confirmPassword: '' });
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up font-sans">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-slate-700">Account Settings</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT: PROFILE CARD */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
                        <div className="w-24 h-24 bg-blue-100 rounded-lg mx-auto flex items-center justify-center text-blue-600 text-4xl font-bold mb-4 overflow-hidden shadow-inner border border-slate-100">
                            {user.logo ? (
                                <img src={user.logo} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <span>{user.initials || 'DH'}</span>
                            )}
                        </div>
                        <h3 className="text-xl font-bold text-slate-800">{user.studio_name || user.username}</h3>
                        <p className="text-xs text-slate-400 mb-6">User ID: #89023</p>

                        <div className="text-left space-y-4 mb-8">
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-xs font-bold text-slate-500 flex items-center gap-2"><User size={14} /> Full Name:</span>
                                <span className="text-xs text-slate-700">{user.studio_name || user.username}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-xs font-bold text-slate-500 flex items-center gap-2"><CheckCircle size={14} /> Status:</span>
                                <span className="text-xs text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded">Active</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-xs font-bold text-slate-500 flex items-center gap-2"><UserCog size={14} /> Role:</span>
                                <span className="text-xs text-slate-700">{user.role || 'Manager'}</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-xs font-bold text-slate-500 flex items-center gap-2"><Settings size={14} /> Access:</span>
                                <span className="text-xs text-slate-700">Full Admin</span>
                            </div>
                            <div className="flex justify-between pt-2">
                                <span className="text-xs font-bold text-slate-500 flex items-center gap-2"><Monitor size={14} /> Last Login:</span>
                                <span className="text-xs text-slate-700">Just now</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button onClick={onEdit} className="flex-1 bg-[#5C5CFF] text-white py-2.5 rounded-md text-xs font-bold uppercase shadow-md hover:bg-[#4b4be6] transition">EDIT DETAILS</button>
                            <button className="flex-1 bg-white border border-red-200 text-red-500 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-red-50 transition">SUSPEND</button>
                        </div>
                    </div>
                </div>

                {/* RIGHT: TABS & CONTENT */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Tabs */}
                    <div className="bg-white rounded-lg p-1 flex gap-2 sm:gap-4 border-b border-slate-200 overflow-x-auto custom-scrollbar no-scrollbar">
                        {['Account', 'Security', 'Consent Forms', 'Settings'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab.toLowerCase().replace(' ', ''))}
                                className={`px-4 sm:px-6 py-3 text-xs font-bold uppercase tracking-wide border-b-2 transition whitespace-nowrap ${activeTab === tab.toLowerCase().replace(' ', '') ? 'border-[#5C5CFF] text-[#5C5CFF]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                            >
                                {tab.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {/* TAB: ACCOUNT */}
                    {activeTab === 'account' && (
                        <div className="space-y-6 animate-fade-in-up">
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                <h3 className="font-bold text-slate-700 mb-6">Account Overview</h3>
                                <div className="grid grid-cols-1 gap-6">
                                    <div className="flex items-center gap-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                                        <div className="w-10 h-10 bg-blue-200 text-blue-600 rounded flex items-center justify-center"><FileText size={20} /></div>
                                        <div><div className="text-xl font-bold text-slate-800">0</div><div className="text-[10px] text-slate-500 uppercase">Consent Forms Signed</div></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: SECURITY (CHANGE PASSWORD) */}
                    {activeTab === 'security' && (
                        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm animate-fade-in-up">
                            <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2"><Lock size={18} /> Change Credentials</h3>
                            <p className="text-xs text-slate-400 mb-6">Update your login email and password here.</p>

                            <div className="space-y-4 max-w-md">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">New Email Address</label>
                                    <input
                                        className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#5C5CFF]"
                                        value={securityForm.email}
                                        readOnly
                                        onChange={e => setSecurityForm({ ...securityForm, email: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">New Password</label>
                                        <input
                                            type="password"
                                            className="w-full border border-slate-300 rounded p-2.5 text-sm focus:border-[#5C5CFF] outline-none"
                                            placeholder="••••••••"
                                            value={securityForm.password}
                                            onChange={e => setSecurityForm({ ...securityForm, password: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Confirm Password</label>
                                        <input
                                            type="password"
                                            className="w-full border border-slate-300 rounded p-2.5 text-sm focus:border-[#5C5CFF] outline-none"
                                            placeholder="••••••••"
                                            value={securityForm.confirmPassword}
                                            onChange={e => setSecurityForm({ ...securityForm, confirmPassword: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="pt-4">
                                    <button onClick={handleUpdateSecurity} className="bg-[#5C5CFF] text-white px-6 py-2.5 rounded-md font-bold text-xs uppercase shadow-lg hover:bg-[#4b4be6] transition">Update Credentials</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: OTHER PLACEHOLDERS */}
                    {(activeTab === 'consentforms' || activeTab === 'settings') && (
                        <div className="bg-white p-12 text-center text-slate-400 border border-dashed border-slate-300 rounded-lg">
                            Feature coming soon for this user profile.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------
// SYSTEM EMAILS BUILDER
// ----------------------------------------------------------------------
// ----------------------------------------------------------------------
// PAGE: AGREEMENT SECTIONS (SYNCED WITH SETTINGS)
// ----------------------------------------------------------------------
const AgreementSectionsPage = ({ user }) => {
    const [sections, setSections] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formStr, setFormStr] = useState({ title: '', content: '' });

    const fetchSections = useCallback(() => {
        const studioId = user?.studio_id || user?.id || 1;
        axios.get(`${API_URL}/form-segments?studio_id=${studioId}`)
            .then(res => setSections(Array.isArray(res.data) ? res.data.filter(s => s.type === 'agreement') : []))
            .catch(console.error);
    }, [user]);

    useEffect(() => { fetchSections(); }, [fetchSections]);

    const handleSave = async () => {
        if (!formStr.title.trim() || !formStr.content.trim()) {
            toast.error("Both Title and Content are required.");
            return;
        }
        try {
            const studioId = user?.studio_id || user?.id || 1;
            if (editing && editing.id) {
                await axios.put(`${API_URL}/form-segments/${editing.id}`, {
                    title: formStr.title,
                    content: formStr.content,
                    is_high_risk: 0,
                    sort_order: editing.sort_order || 0,
                    is_active: 1
                });
            } else {
                await axios.post(`${API_URL}/form-segments`, {
                    type: 'agreement',
                    title: formStr.title,
                    content: formStr.content,
                    studio_id: studioId,
                    is_high_risk: 0,
                    sort_order: sections.length
                });
            }
            setEditing(null);
            setFormStr({ title: '', content: '' });
            fetchSections();
        } catch (e) {
                        toast.error(e.response?.data?.message || "Error saving agreement");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this section?")) return;
        try { 
            await axios.delete(`${API_URL}/form-segments/${id}`); 
            fetchSections(); 
        } catch (e) { 
                        toast.error(e.response?.data?.message || "Error deleting segment"); 
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-slate-700">Legal Acknowledgments</h2>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">Syncs with Settings → Agreements and the Consent Form.</p>
                </div>
            </div>

            <div className="bg-white p-5 sm:p-6 rounded-lg shadow-sm border border-slate-200">
                <h3 className="font-bold mb-4 text-slate-700 text-sm uppercase tracking-wider">{editing ? 'Edit Section' : 'Add New Section'}</h3>
                <input className="w-full border border-slate-200 p-3 rounded mb-3 text-sm focus:outline-none focus:border-[#5C5CFF]" placeholder="Title (e.g. Terms & Conditions)" value={formStr.title} onChange={e => setFormStr({ ...formStr, title: e.target.value })} />
                <textarea className="w-full border border-slate-200 p-3 rounded mb-4 h-32 text-sm focus:outline-none focus:border-[#5C5CFF]" placeholder="Content..." value={formStr.content} onChange={e => setFormStr({ ...formStr, content: e.target.value })} />
                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={handleSave} className="bg-[#5C5CFF] text-white px-6 py-2.5 rounded font-bold text-xs uppercase shadow-sm hover:bg-[#4b4be6]">SAVE SECTION</button>
                    {editing && <button onClick={() => { setEditing(null); setFormStr({ title: '', content: '' }); }} className="text-slate-500 text-sm font-bold uppercase hover:underline p-2">Cancel</button>}
                </div>
            </div>

            <div className="grid gap-4">
                {sections.map(s => (
                    <div key={s.id} className="bg-white p-5 sm:p-6 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div>
                            <div className="font-bold text-lg text-slate-800 mb-1">{s.title}</div>
                            <div className="text-sm text-slate-500 whitespace-pre-wrap leading-relaxed">{s.content}</div>
                        </div>
                        <div className="flex gap-2 ml-4">
                            {(s.studio_id === (user?.studio_id || user?.id) || user?.role === 'superadmin') ? (
                                <>
                                    <button onClick={() => { setEditing(s); setFormStr({ title: s.title, content: s.content }); }} className="text-slate-400 hover:text-blue-500 bg-slate-50 p-2 rounded"><Edit size={16} /></button>
                                    <button onClick={() => handleDelete(s.id)} className="text-slate-400 hover:text-red-500 bg-slate-50 p-2 rounded"><Trash2 size={16} /></button>
                                </>
                            ) : (
                                <span className="text-[10px] font-bold text-slate-300 bg-slate-50 px-2 py-1 rounded uppercase tracking-wider self-start mt-2">System</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------
// PAGE: HEALTH SECTIONS (SYNCED WITH SETTINGS)
// ----------------------------------------------------------------------
const HealthSectionsPage = ({ user }) => {
    const [sections, setSections] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formStr, setFormStr] = useState({ content: '', is_high_risk: 0 });

    const fetchSections = useCallback(() => {
        const studioId = user?.studio_id || user?.id || 1;
        axios.get(`${API_URL}/form-segments?studio_id=${studioId}`)
            .then(res => setSections(Array.isArray(res.data) ? res.data.filter(s => s.type === 'health') : []))
            .catch(console.error);
    }, [user]);

    useEffect(() => { fetchSections(); }, [fetchSections]);

    const handleSave = async () => {
        if (!formStr.content.trim()) {
            toast.error("Please enter a question.");
            return;
        }
        try {
            const studioId = user?.studio_id || user?.id || 1;
            if (editing && editing.id) {
                await axios.put(`${API_URL}/form-segments/${editing.id}`, {
                    title: 'Health Question',
                    content: formStr.content,
                    is_high_risk: formStr.is_high_risk,
                    sort_order: editing.sort_order || 0,
                    is_active: 1
                });
            } else {
                await axios.post(`${API_URL}/form-segments`, {
                    type: 'health',
                    title: 'Health Question',
                    content: formStr.content,
                    studio_id: studioId,
                    is_high_risk: formStr.is_high_risk,
                    sort_order: sections.length
                });
            }
            setEditing(null);
            setFormStr({ content: '', is_high_risk: 0 });
            fetchSections();
        } catch (e) {
                        toast.error(e.response?.data?.message || "Error saving question");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try { 
            await axios.delete(`${API_URL}/form-segments/${id}`); 
            fetchSections(); 
        } catch (e) { 
            console.error("Delete error:", e);
            toast.error(e.response?.data?.message || "Error deleting segment"); 
        }
    };

    const toggleHighRisk = async (section) => {
        try {
            await axios.put(`${API_URL}/form-segments/${section.id}`, {
                ...section,
                is_high_risk: section.is_high_risk ? 0 : 1
            });
            fetchSections();
        } catch (e) {
            toast.error("Error updating");
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-slate-700">Health Screening</h2>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">Managed health questions for your consent forms.</p>
                </div>
            </div>

            <div className="bg-white p-5 sm:p-6 rounded-lg shadow-sm border border-slate-200">
                <h3 className="font-bold mb-4 text-slate-700 text-sm uppercase tracking-wider">{editing ? 'Edit Question' : 'Add New Question'}</h3>
                <input
                    className="w-full border border-slate-200 p-3 rounded mb-3 text-sm focus:outline-none focus:border-[#5C5CFF]"
                    placeholder="Enter health screening question..."
                    value={formStr.content}
                    onChange={e => setFormStr({ ...formStr, content: e.target.value })}
                />
                <label className="flex items-center gap-3 mb-6 cursor-pointer bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <input
                        type="checkbox"
                        checked={formStr.is_high_risk === 1}
                        onChange={e => setFormStr({ ...formStr, is_high_risk: e.target.checked ? 1 : 0 })}
                        className="w-5 h-5 rounded text-red-500 focus:ring-red-500 border-slate-300"
                    />
                    <span className="text-sm font-bold text-red-600">⚠️ High-Risk Alert</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={handleSave} className="bg-[#5C5CFF] text-white px-6 py-2.5 rounded font-bold text-xs uppercase shadow-sm hover:bg-[#4b4be6]">SAVE QUESTION</button>
                    {editing && <button onClick={() => { setEditing(null); setFormStr({ content: '', is_high_risk: 0 }); }} className="text-slate-500 text-sm font-bold uppercase hover:underline p-2">Cancel</button>}
                </div>
            </div>

            <div className="grid gap-3">
                {sections.map((s, idx) => (
                    <div key={s.id} className={`bg-white p-4 sm:p-5 rounded-lg shadow-sm border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${s.is_high_risk ? 'border-red-200 bg-red-50/50' : 'border-slate-200'}`}>
                        <div className="flex items-start sm:items-center gap-4 w-full">
                            <span className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 font-bold text-[10px] shrink-0">{idx + 1}</span>
                            <div className="flex-1">
                                <div className="text-sm font-medium text-slate-700 leading-relaxed">{s.content}</div>
                                {s.is_high_risk === 1 && <span className="inline-block mt-1 text-[9px] bg-red-600 text-white px-2 py-0.5 rounded font-black uppercase tracking-wider">High Risk</span>}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-none border-slate-100">
                            {(s.studio_id === (user?.studio_id || user?.id) || user?.role === 'superadmin') ? (
                                <>
                                    <button onClick={() => toggleHighRisk(s)} className={`p-2 rounded-md transition ${s.is_high_risk ? 'bg-red-200 text-red-700' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`} title="Toggle High-Risk"><Activity size={16} /></button>
                                    <button onClick={() => { setEditing(s); setFormStr({ content: s.content, is_high_risk: s.is_high_risk }); }} className="p-2 text-slate-400 hover:text-[#666CFF] bg-slate-50 hover:bg-indigo-50 rounded-md transition"><Edit size={16} /></button>
                                    <button onClick={() => handleDelete(s.id)} className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded-md transition"><Trash2 size={16} /></button>
                                </>
                            ) : (
                                <span className="text-[10px] font-bold text-slate-300 bg-slate-50 px-2 py-1 rounded uppercase tracking-wider self-start">System Question</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};




// ----------------------------------------------------------------------
// SENT FORMS PAGE
// ----------------------------------------------------------------------
const SentFormsPage = ({ clients, onViewClient }) => {
    const downloadCSV = () => {
        const headers = ["First Name", "Last Name", "Email", "Phone", "Date", "Staff", "Price", "Consent ID"];
        const rows = clients.map(c => [
            c.first_name, c.last_name, c.email, c.phone,
            c.appt_date ? new Date(c.appt_date).toLocaleDateString() : '',
            c.staff_name || 'Unassigned',
            c.price || '0',
            c.consent_id || ''
        ]);
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "clients_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-slate-700">Sent Forms Archive</h2>
                    <p className="text-xs text-slate-400">View and manage all submitted consent forms.</p>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                    <button onClick={downloadCSV} className="w-full sm:w-auto bg-white border border-slate-300 text-slate-600 px-4 py-2 rounded-md font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2 transition shadow-sm">
                        <FileText size={16} /> Export CSV
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-[#f8f9fa] text-xs uppercase font-bold text-slate-500 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4">Customer Name</th>
                            <th className="px-6 py-4">Phone</th>
                            <th className="px-6 py-4">Appointment Date</th>
                            <th className="px-6 py-4">Staff</th>
                            <th className="px-6 py-4">Total Price</th>
                            <th className="px-6 py-4">Agreements</th>
                            <th className="px-6 py-4 text-center">Status</th>
                            <th className="px-6 py-4 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {clients.map(c => (
                            <tr key={c.id} className="hover:bg-slate-50">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-slate-700">{c.first_name} {c.last_name}</div>
                                    <div className="text-[10px] text-blue-500 font-mono font-bold">Ref: #{c.consent_id || 'N/A'}</div>
                                    <div className="text-xs text-slate-400">Email: {c.email}</div>
                                </td>
                                <td className="px-6 py-4">{c.phone}</td>
                                <td className="px-6 py-4">{c.appt_date ? new Date(c.appt_date).toLocaleDateString() : '-'}</td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getColorClass(c.staff_name || 'Unassigned')}`}>
                                            {getInitials(c.staff_name || 'Unassigned')}
                                        </div>
                                        {c.staff_name || 'Unassigned'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 font-bold text-slate-700">£{c.price || '0.00'}</td>
                                <td className="px-6 py-4">
                                    <div className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded inline-flex items-center gap-1">
                                        <Check size={10} className="text-green-500" />
                                        {(() => {
                                            const ags = Array.isArray(c.agreements) ? c.agreements : (typeof c.agreements === 'string' ? JSON.parse(c.agreements || '[]') : []);
                                            return Array.isArray(ags) ? ags.length : 1;
                                        })()} Accepted
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    {(c.status === 'signed' || c.signature) ? (
                                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase">SIGNED</span>
                                    ) : (
                                        <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-bold uppercase">PENDING</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button onClick={() => onViewClient(c)} className="bg-purple-100 text-purple-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase">View Details</button>
                                </td>
                            </tr>
                        ))}
                        {clients.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-400">No sent forms found.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------
// DASHBOARD CONTAINER (UPDATED FOR PROFILE & PERSISTENCE)
// ----------------------------------------------------------------------
function Dashboard({
    user, clients, staffList, consentForms, campaigns,
    allStudios, selectedStudioId, setSelectedStudioId,
    onLogout, onRefresh, onOpenPublicForm, setUser
}) {
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [currentPage, setCurrentPage] = useState(() => {
        // Restore last page from localStorage
        const savedPage = localStorage.getItem('bookink_currentPage');
        return savedPage || 'dashboard';
    });

    // Save current page to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('bookink_currentPage', currentPage);
    }, [currentPage]);

    const isSuperAdmin = user.role === 'superadmin';

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedForm, setSelectedForm] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [clientToEdit, setClientToEdit] = useState(null);
    const [staffToEdit, setStaffToEdit] = useState(null);

    const [showClientModal, setShowClientModal] = useState(false);
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showStudioModal, setShowStudioModal] = useState(false);
    const [showEditStudioModal, setShowEditStudioModal] = useState(false);
    const [showSendFormModal, setShowSendFormModal] = useState(false);
    const [selectedFormForSending, setSelectedFormForSending] = useState(null);
    const [studioToEdit, setStudioToEdit] = useState(null);
    const [showStudioQR, setShowStudioQR] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    // RBAC: Filter Data based on Role
    const visibleClients = filterClientsByRole(clients, user);
    const visibleForms = filterFormsByRole(consentForms, user);

    // Global Search Logic
    const searchedClients = visibleClients.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.phone && c.phone.includes(searchTerm)) ||
        (c.consent_id && c.consent_id.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const searchedStaffItems = staffList.filter(s =>
        (s.name && s.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // RBAC: Route Guard - Redirect if staff tries to access restricted pages
    useEffect(() => {
        if (isPageRestricted(currentPage, user)) {
            setCurrentPage('dashboard');
        }
    }, [currentPage, user]);

    const handleViewClient = (c) => { setSelectedClient(c); setCurrentPage('clientDetails'); };
    const handleViewSentForm = (c) => { setSelectedClient(c); setCurrentPage('sentFormDetails'); };
    const handleEditClient = (c) => { setClientToEdit(c); setShowClientModal(true); };
    const handleViewForm = (f) => { setSelectedForm(f); setCurrentPage('formDetails'); };
    const handleViewProfile = (s) => { setSelectedStaff(s); setCurrentPage('staffDetails'); };
    const handleDeleteStaff = async (staff) => {
        if (!window.confirm(`Are you sure you want to delete ${staff.name}?`)) return;
        try {
            await axios.delete(`${API_URL}/staff/${staff.id}`);
            onRefresh();
        } catch (e) {
            toast.error(e.response?.data?.message || "Error deleting staff");
        }
    };

    const renderContent = () => {
        switch (currentPage) {
            case 'dashboard': return <DashboardHome user={user} clients={searchedClients} staffList={staffList} consentForms={visibleForms} studios={allStudios} isSuperAdmin={isSuperAdmin} onChangePage={setCurrentPage} onAddClient={() => { setClientToEdit(null); setShowClientModal(true); }} onSendForm={() => { setSelectedFormForSending(visibleForms.find(f => f.is_default) || visibleForms[0]); setShowSendFormModal(true); }} onViewClient={handleViewSentForm} onEditClient={handleEditClient} onSelectStudio={(id) => setSelectedStudioId(id)} onOpenPublicForm={onOpenPublicForm} onOpenStudioQR={() => setShowStudioQR(true)} />;
            case 'superAdmin': return <SuperAdminDashboard />;
            case 'sentForms': return <SentFormsPage clients={searchedClients} onViewClient={handleViewSentForm} />;
            case 'clientDetails': return <CustomerDetailsPage client={selectedClient} onBack={() => setCurrentPage('dashboard')} onRefresh={onRefresh} onChangePage={setCurrentPage} />;
            case 'sentFormDetails': return <SentFormDetailPage client={selectedClient} studios={allStudios} onBack={() => setCurrentPage('sentForms')} onRefresh={onRefresh} />;
            case 'staff': return <StaffPage staffList={searchedStaffItems} onAdd={() => { setStaffToEdit(null); setShowStaffModal(true); }} onEdit={(s) => { setStaffToEdit(s); setShowStaffModal(true); }} onDelete={handleDeleteStaff} onViewProfile={handleViewProfile} />;
            case 'staffDetails': return <StaffDetailsPage staff={selectedStaff} clients={clients} onBack={() => setCurrentPage('staff')} onRefresh={onRefresh} />;
            case 'marketing': return <MarketingPage campaigns={campaigns} onStart={() => setShowCampaignModal(true)} />;
            case 'consentForms': return <ConsentFormsListPage forms={visibleForms} onCreate={() => setShowFormModal(true)} onSend={(f) => { setSelectedFormForSending(f); setShowSendFormModal(true); }} onView={(f) => { setSelectedForm(f); setCurrentPage('formDetails'); }} onRefresh={onRefresh} />;
            case 'formDetails': return <ConsentFormDetailPage user={user} form={selectedForm} isSuperAdmin={isSuperAdmin} onBack={() => { onRefresh(); setCurrentPage('consentForms'); }} onOpenPublicForm={(updatedForm) => onOpenPublicForm(updatedForm || selectedForm)} onRefresh={onRefresh} />;
            case 'agreements': return <AgreementSectionsPage user={user} />;
            case 'health': return <HealthSectionsPage user={user} />;
            case 'emailSettings': return <EmailSettingsPage user={user} />;
            case 'settings': return <SettingsPage user={user} onNavigate={setCurrentPage} />;
            case 'profile': return <UserProfilePage user={user} onEdit={() => {
                if (user.role === 'studio' || user.role === 'superadmin') {
                    // Admins edit their studio details
                    setStudioToEdit(user);
                    setShowEditStudioModal(true);
                } else {
                    // Staff edit their staff record - IMPORTANT: Number() cast to avoid type mismatch
                    const staffRecord = staffList.find(s => Number(s.id) === Number(user.id));
                    if (staffRecord) {
                        setStaffToEdit(staffRecord);
                        setShowStaffModal(true);
                    } else {
                        // Fallback if record not in staffList yet
                        setStaffToEdit({
                            id: user.id,
                            first_name: user.username?.split(' ')[0] || '',
                            last_name: user.username?.split(' ').slice(1).join(' ') || '',
                            email: user.email,
                            role: user.role,
                            studio_id: user.studio_id
                        });
                        setShowStaffModal(true);
                    }
                }
            }} />;
            // NEW ROUTE
            default: return <DashboardHome user={user} clients={searchedClients} staffList={staffList} consentForms={visibleForms} studios={allStudios} isSuperAdmin={isSuperAdmin} onChangePage={setCurrentPage} onAddClient={() => { setClientToEdit(null); setShowClientModal(true); }} onSendForm={() => { setSelectedFormForSending(visibleForms.find(f => f.is_default) || visibleForms[0]); setShowSendFormModal(true); }} onViewClient={handleViewSentForm} onEditClient={handleEditClient} onSelectStudio={(id) => setSelectedStudioId(id)} onOpenPublicForm={onOpenPublicForm} onOpenStudioQR={() => setShowStudioQR(true)} />;
        }
    };

    return (
        <div className="flex h-screen bg-[#f3f4f6] font-sans text-slate-600 relative">
            <ToastContainer />
            {showClientModal && <ClientModal user={user} onClose={() => setShowClientModal(false)} onRefresh={onRefresh} client={clientToEdit} staffList={staffList} />}
            {showSendFormModal && <SendConsentFormModal user={user} clients={visibleClients} form={selectedFormForSending} selectedStudioId={selectedStudioId} onClose={() => { setShowSendFormModal(false); setSelectedFormForSending(null); }} onRefresh={onRefresh} />}
            {showStaffModal && <StaffModal user={user} onClose={() => setShowStaffModal(false)} onRefresh={onRefresh} staff={staffToEdit} />}
            {showCampaignModal && <CampaignModal user={user} onClose={() => setShowCampaignModal(false)} onRefresh={onRefresh} staffList={staffList} />}
            {showFormModal && <CreateConsentFormModal user={user} isSuperAdmin={isSuperAdmin} onClose={() => setShowFormModal(false)} onRefresh={onRefresh} />}
            {showStudioModal && <AddStudioModal onClose={() => setShowStudioModal(false)} onRefresh={onRefresh} />}
            {showEditStudioModal && <EditStudioModal studio={studioToEdit} user={user} setUser={setUser} onClose={() => setShowEditStudioModal(false)} onRefresh={onRefresh} />}
            {showStudioQR && <QRCodeModal 
                form={{ id: visibleForms[0]?.id, studio_id: user?.studio_id || user?.id, title: user.studio_name }} 
                onClose={() => setShowStudioQR(false)} 
                customUrl={visibleForms[0] ? `https://consent.bookink.uk/?formId=${visibleForms[0].id}&studio_id=${user?.studio_id || user?.id}` : undefined}
                title="Studio QR Code"
            />}

            <aside className={`
                w-64 bg-slate-800 border-r border-slate-700 flex flex-col print:hidden
                fixed inset-y-0 left-0 z-[100] transform transition-transform duration-300 md:relative md:translate-x-0
                ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="h-24 flex items-center justify-between px-4 border-b border-slate-700">
                    <div className="flex items-center gap-3 w-full cursor-pointer touch-manipulation" onClick={() => { setCurrentPage('dashboard'); setShowMobileSidebar(false); }}>
                        {user.logo ? (
                            <img src={user.logo} alt="Studio Logo" className="w-12 h-12 rounded-lg object-contain" />
                        ) : (
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#5C5CFF] to-[#8E8EFF] flex items-center justify-center text-white font-black text-xl shadow-lg">
                                {(user.studio_name || 'S')[0].toUpperCase()}
                            </div>
                        )}
                        <div className="flex flex-col">
                            <span className="font-black text-white tracking-tight leading-tight text-base">{user.studio_name || 'My Studio'}</span>
                            <span className="text-[10px] font-bold text-purple-300 uppercase tracking-tight">Powered by BookINK</span>
                        </div>
                    </div>
                    {/* MOBILE CLOSE BUTTON */}
                    <button onClick={() => setShowMobileSidebar(false)} className="md:hidden text-slate-400 hover:text-white p-2">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    <SidebarItem icon={<LayoutDashboard size={18} />} label="Dashboard" active={currentPage === 'dashboard' || currentPage === 'clientDetails'} onClick={() => { setCurrentPage('dashboard'); setShowMobileSidebar(false); }} dark />
                    <SidebarItem icon={<Send size={18} />} label="Sent Forms" active={currentPage === 'sentForms' || currentPage === 'sentFormDetails'} onClick={() => { setCurrentPage('sentForms'); setShowMobileSidebar(false); }} dark />


                    {isOwnerOrAdmin(user) && (
                        <>
                            {isSuperAdmin && (
                                <SidebarItem icon={<Shield size={18} />} label="Super Admin" active={currentPage === 'superAdmin'} onClick={() => { setCurrentPage('superAdmin'); setShowMobileSidebar(false); }} dark color="text-purple-400" />
                            )}
                            {/* Consolidated into Super Admin Dashboard */}
                            <SidebarItem icon={<UserCog size={18} />} label="Manage Staff" active={currentPage === 'staff'} onClick={() => { setCurrentPage('staff'); setShowMobileSidebar(false); }} dark />
                            <SidebarItem icon={<Megaphone size={18} />} label="Marketing" active={currentPage === 'marketing'} onClick={() => { setCurrentPage('marketing'); setShowMobileSidebar(false); }} dark />
                        </>
                    )}
                    {/* Consolidated Platform Admin Items */}

                    <div className="pt-6 pb-2 px-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Settings Area</div>
                    <SidebarItem icon={<FileText size={18} />} label="Consent Forms" active={currentPage === 'consentForms' || currentPage === 'formDetails'} onClick={() => { setCurrentPage('consentForms'); setShowMobileSidebar(false); }} dark />

                    {isOwnerOrAdmin(user) && (
                        <>
                            <SidebarItem icon={<Shield size={18} />} label="Agreement Sections" active={currentPage === 'agreements'} onClick={() => { setCurrentPage('agreements'); setShowMobileSidebar(false); }} dark />
                            <SidebarItem icon={<Activity size={18} />} label="Health Sections" active={currentPage === 'health'} onClick={() => { setCurrentPage('health'); setShowMobileSidebar(false); }} dark />
                            <SidebarItem icon={<Mail size={18} />} label="Email Branding" active={currentPage === 'emailSettings'} onClick={() => { setCurrentPage('emailSettings'); setShowMobileSidebar(false); }} dark />
                        </>
                    )}

                    <div className="pt-6 pb-2 px-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Studio</div>
                    <SidebarItem icon={<Settings size={18} />} label={isOwnerOrAdmin(user) ? "Studio Settings" : "My Settings"} active={currentPage === 'settings'} onClick={() => { setCurrentPage('settings'); setShowMobileSidebar(false); }} dark />
                </div>
                <div className="p-4 border-t border-slate-700">
                    <button onClick={onLogout} className="flex items-center gap-3 text-slate-300 hover:text-red-400 transition px-3 py-2 w-full text-left rounded-md touch-manipulation">
                        <LogOut size={18} /> <span className="text-sm font-medium">Sign Out</span>
                    </button>
                </div>
            </aside>

            <main className="flex-1 flex flex-col overflow-hidden relative">
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 z-10 print:hidden relative">
                    <div className="flex items-center gap-4">
                        {/* HAMBURGER MENU BUTTON */}
                        <button onClick={() => setShowMobileSidebar(true)} className="md:hidden text-slate-600 p-1 hover:bg-slate-50 rounded-md">
                            <Menu size={24} />
                        </button>

                        <div className="hidden md:flex items-center text-slate-400">
                            <Search size={18} />
                            <input
                                className="ml-3 bg-transparent outline-none text-sm w-64 text-slate-600 placeholder-slate-400"
                                placeholder="Search everything..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                            <div className="text-sm font-bold text-slate-700">{user.studio_name || user.username}</div>
                            <div className="text-xs text-[#666CFF] font-bold uppercase">{user.role === 'superadmin' ? 'Platform Admin' : user.role === 'studio' ? 'Studio Admin' : 'Artist'}</div>
                        </div>
                        {/* CLICKABLE AVATAR -> PROFILE PAGE */}
                        <div onClick={() => setCurrentPage('profile')} className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs cursor-pointer hover:ring-2 hover:ring-blue-300 transition overflow-hidden">
                            {user.logo ? (
                                <img src={user.logo} alt="Av" className="w-full h-full object-cover" />
                            ) : (
                                <span>{user.initials || 'AD'}</span>
                            )}
                        </div>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-4 md:p-6">{renderContent()}</div>

                {/* MOBILE SIDEBAR OVERLAY */}
                {showMobileSidebar && (
                    <div
                        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[90] md:hidden animate-fade-in"
                        onClick={() => setShowMobileSidebar(false)}
                    ></div>
                )}
            </main>
        </div>
    );
}

// ----------------------------------------------------------------------
// DASHBOARD HOME
// ----------------------------------------------------------------------
const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase() : 'UN';

const getColorClass = (name) => {
    if (!name) return 'bg-slate-100 text-slate-400';
    const colors = [
        'bg-green-100 text-green-600',
        'bg-pink-100 text-pink-600',
        'bg-red-100 text-red-600',
        'bg-purple-100 text-purple-600',
        'bg-blue-100 text-blue-600',
        'bg-yellow-100 text-yellow-600',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

// (Mockup DashboardHome removed, now using features/dashboard/DashboardHome)



// ... (KEEP ALL EXISTING COMPONENTS: ConsentFormDetailPage, PublicSigningPage, etc. EXACTLY AS THEY WERE) ...
// TO SAVE SPACE, I AM RE-INSERTING THE KEY PAGES WE DISCUSSED PREVIOUSLY BELOW.
// YOU MUST KEEP PublicSigningPage, ClientDetailsPage, etc. IN THE FILE.

// --- PAGE: PUBLIC SIGNING VIEW (WITH PRICE INPUT) ---
const PublicSigningPage = ({ form, onBack, ownerStudioId }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [segments, setSegments] = useState([]); // Dynamic segments
    const videoRef = useRef(null); // Camera video reference
    const mainRef = useRef(null);
    const [activeSection, setActiveSection] = useState('section-appointment');

    const scrollToSection = (id) => {
        const main = mainRef.current;
        const target = document.getElementById(id);
        if (!main || !target) return;
        const mainRect = main.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        main.scrollBy({ top: targetRect.top - mainRect.top - 24, behavior: 'smooth' });
    };

    useEffect(() => {
        const main = mainRef.current;
        if (!main) return;
        const sections = ['section-appointment', 'section-agreement', 'section-health', 'section-signature'];
        const handleScroll = () => {
            const mainRect = main.getBoundingClientRect();
            for (const id of sections) {
                const el = document.getElementById(id);
                if (!el) continue;
                const elRect = el.getBoundingClientRect();
                if (elRect.top - mainRect.top <= 60) {
                    setActiveSection(id);
                }
            }
        };
        main.addEventListener('scroll', handleScroll, { passive: true });
        return () => main.removeEventListener('scroll', handleScroll);
    }, []);

    // Capture Customer Inputs
    const [formData, setFormData] = useState({
        first_name: form?.prefillData?.first_name || '',
        middle_name: '',
        last_name: form?.prefillData?.last_name || '',
        phone: '',
        email: form?.prefillData?.email || '',
        address: '',
        postcode: '',
        dob: '',
        age: '',
        staff_name: form?.prefillData?.staff_name || '',
        price: form?.prefillData?.price || ''
    });

    // Sync formData if prefillData arrives late
    useEffect(() => {
        if (form?.prefillData) {
            setFormData(prev => ({
                ...prev,
                first_name: form.prefillData.first_name || prev.first_name,
                last_name: form.prefillData.last_name || prev.last_name,
                email: form.prefillData.email || prev.email,
                staff_name: form.prefillData.staff_name || prev.staff_name,
                price: form.prefillData.price || prev.price
            }));
        }
    }, [form?.prefillData]);

    const [selectedHealth, setSelectedHealth] = useState([]);
    const [healthDetails, setHealthDetails] = useState(''); // Details for health conditions
    const [selectedAgreements, setSelectedAgreements] = useState([]);
    const [studioDetails, setStudioDetails] = useState(null);
    const [mediaConsent, setMediaConsent] = useState(true); // Media release toggle (on by default)
    const [instagramHandle, setInstagramHandle] = useState(''); // Instagram handle
    const [idPhoto, setIdPhoto] = useState(null); // ID photo capture
    const [showCamera, setShowCamera] = useState(false); // Camera modal

    const getStaffDisplayName = (staff) => {
        if (!staff) return 'Unknown Artist';
        return staff.name || [staff.first_name, staff.last_name].filter(Boolean).join(' ') || staff.artist_name || 'Unknown Artist';
    };

    // Auto-redirect back to dashboard after submission (3 second delay)

    // Stop camera stream when component unmounts (prevents browser camera indicator staying on)
    useEffect(() => {
        return () => {
            if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Check if any red flag condition is selected
    const hasRedFlags = selectedHealth.some(c => c.is_high_risk);

    useEffect(() => {
        if (form?.studio_id) {
            axios.get(`${API_URL}/studios/${form.studio_id}`)
                .then(res => setStudioDetails(res.data))
                .catch(() => {});
        }
    }, [form?.studio_id]);

    const toggleAgreement = (item) => {
        const isString = typeof item === 'string';
        setSelectedAgreements(prev => {
            const exists = isString
                ? prev.includes(item)
                : prev.some(s => s.id === item.id);

            if (exists) {
                return isString
                    ? prev.filter(s => s !== item)
                    : prev.filter(s => s.id !== item.id);
            } else {
                return [...prev, item];
            }
        });
    };

    useEffect(() => {
        // Prefer the explicit studio attached to the public form.
        // Only fall back to a logged-in user's studio when the form itself does not specify one.
        let studioId = form?.studio_id || form?.studioDetails?.id || ownerStudioId;
        if ((!studioId || studioId === 1) && !form?.isTokenWorkflow) {
            try {
                const t = localStorage.getItem('bookink_token');
                if (t) studioId = JSON.parse(atob(t.split('.')[1])).studio_id || studioId;
            } catch(e) {}
        }

        if (form?.isTokenWorkflow && Array.isArray(form?.artists) && form.artists.length > 0) {
            setStaffList(form.artists);
        } else if (studioId) {
            axios.get(`${API_URL}/public/staff?studio_id=${studioId}`).then(res => setStaffList(Array.isArray(res.data) ? res.data : [])).catch(console.error);
        } else {
            setStaffList([]);
        }

        // Fetch Dynamic Form Segments
        axios.get(`${API_URL}/form-segments?studio_id=${studioId}`)
            .then(res => setSegments(res.data))
            .catch(console.error);
    }, [form, ownerStudioId]);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (fieldErrors[name]) setFieldErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
        let updatedData = { ...formData, [name]: value };

        // Auto-Calculate Age based on DOB
        if (name === 'dob' && value) {
            const birthDate = new Date(value);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            updatedData.age = age.toString();
        }

        setFormData(updatedData);
    };

    const toggleHealth = (item) => {
        const isString = typeof item === 'string';
        setSelectedHealth(prev => {
            const exists = isString
                ? prev.includes(item)
                : prev.some(s => s.id === item.id);

            if (exists) {
                return isString
                    ? prev.filter(s => s !== item)
                    : prev.filter(s => s.id !== item.id);
            } else {
                return [...prev, item];
            }
        });
    };

    // SAVE LOGIC
    const handleSubmit = async () => {
        const errs = {};
        if (!formData.staff_name) errs.staff_name = true;
        if (!formData.first_name) errs.first_name = true;
        if (!formData.last_name) errs.last_name = true;
        if (!formData.price) errs.price = true;
        if (!formData.address) errs.address = true;
        if (!formData.postcode) errs.postcode = true;
        if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) errs.phone = true;
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = true;
        if (!formData.age) errs.age = true;

        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            const labels = { staff_name: 'Artist', first_name: 'First Name', last_name: 'Surname', price: 'Price', address: 'Address', postcode: 'Postcode', phone: 'Phone Number', email: 'Email Address', age: 'Age' };
            toast.error(`Please fix: ${Object.keys(errs).map(k => labels[k]).join(', ')}`);
            return;
        }
        setFieldErrors({});

        const allAgreements = form?.agreements || [];
        if (allAgreements.length > 0 && selectedAgreements.length < allAgreements.length) {
            toast.error("Please accept all agreements."); return;
        }
        const sigCanvas = canvasRef.current;
        const sigPixels = sigCanvas.getContext('2d').getImageData(0, 0, sigCanvas.width, sigCanvas.height).data;
        if (!sigPixels.some((v, i) => i % 4 === 3 && v > 0)) {
            toast.error("Please provide your signature."); return;
        }

        const confirmMsg = `Review your submission:\n\n` +
            `- ${selectedAgreements.length} Agreements Accepted\n` +
            `- ${selectedHealth.length} Health Conditions Reported\n\n` +
            `Submit this consent form now?`;

        if (!window.confirm(confirmMsg)) return;

        setLoading(true);
        const signatureData = canvasRef.current.toDataURL("image/png");

        // GENERATE PDF
        const pdfDoc = generateConsentPDF({
            ...formData,
            signature: signatureData,
            agreements: selectedAgreements,
            health_conditions: selectedHealth,
            health_details: healthDetails,
            has_red_flags: hasRedFlags,
            id_photo: idPhoto,
            media_consent: mediaConsent,
            instagram_handle: instagramHandle,
            appt_date: new Date().toISOString(),
            signed_at: new Date().toISOString(),
            consent_id: 'NEW',
        }, studioDetails); // Pass studio details
        const pdfBase64 = pdfDoc.output('datauristring').split(',')[1];

        try {
            await axios.post(`${API_URL}/clients`, {
                    id: null,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    phone: formData.phone,
                    appt_date: formData.appt_date || new Date().toISOString().slice(0, 19).replace('T', ' '),
                    staff_name: formData.staff_name || 'Unassigned',
                    price: formData.price,
                    address: formData.address,
                    postcode: formData.postcode,
                    dob: formData.dob,
                    age: formData.age,
                    signature: signatureData,
                    health_conditions: selectedHealth,
                    health_details: healthDetails, // NEW: Health details text
                    has_red_flags: hasRedFlags ? 1 : 0, // NEW: Red flag indicator
                    agreements: selectedAgreements,
                    studio_id: form?.studio_id || 1,
                    pdf_base64: pdfBase64,
                    id_photo: idPhoto, // NEW: ID photo
                    media_consent: mediaConsent ? 1 : 0, // NEW: Media consent
                    instagram_handle: instagramHandle, // NEW: Instagram handle
                    signed_at: new Date().toISOString().slice(0, 19).replace('T', ' ') // NEW: Signed timestamp
                });

            setSubmitted(true);
        } catch (error) {
                        toast.error("Failed to save. Ensure your server is running.");
        } finally {
            setLoading(false);
        }
    };


    if (submitted) return (
        <div className="flex h-screen bg-[#F5F7FA] items-center justify-center animate-fade-in-up">
            <div className="bg-white p-12 rounded-2xl shadow-xl text-center max-w-lg w-full border border-slate-100">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={48} className="text-green-600" />
                </div>
                <h2 className="text-3xl font-black text-slate-800 mb-4 tracking-tight">You're All Set!</h2>
                <p className="text-slate-500 leading-relaxed">Your consent form has been securely saved. Please return this device to the front desk.</p>
            </div>
        </div>
    );

    // Signature Logic
    // Signature Logic - Enhanced for Smoothness & Touch
    const getCoords = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        // Handle Touch Events
        if (e.touches && e.touches[0]) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        // Handle Mouse Events
        return {
            x: e.nativeEvent.offsetX,
            y: e.nativeEvent.offsetY
        };
    };

    const startDrawing = (e) => {
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getCoords(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getCoords(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const endDrawing = () => setIsDrawing(false);

    const clearSignature = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    const displayTitle = form?.title || "Tattoo Consent Form";
    const displayDesc = form?.description || "Please read this Consent Form carefully...";
    const displayAgreements = form?.agreements || [];
    // eslint-disable-next-line no-unused-vars
    const displayHealth = form?.health_conditions || [];

    // Kiosk Mode Exit
    const handleExitKiosk = () => {
        try {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { /* Safari */
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) { /* Firefox */
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) { /* IE/Edge */
                document.msExitFullscreen();
            }
        } catch (e) { console.warn("Exit fullscreen failed", e); }
        onBack();
    };

    return (
        <div className="flex h-screen bg-[#F5F7FA] font-sans overflow-hidden animate-fade-in-up">
            <ToastContainer />
            {/* Sidebar - HIDDEN IN KIOSK MODE */}
            {!form?.isKiosk && (
                <aside className="w-72 bg-[#4F46E5] text-white flex flex-col p-8 relative hidden md:flex print:hidden">
                    <div className="mb-12">
                        <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center mb-4 mx-auto border-2 border-white/20 overflow-hidden bg-slate-50">
                            {studioDetails?.logo ? (
                                <img src={studioDetails.logo} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <span className="font-bold text-lg text-[#4F46E5]">{(studioDetails?.studio_name || "B")[0]}</span>
                            )}
                        </div>
                        <div className="text-center">
                            <h2 className="font-bold text-sm uppercase tracking-wider">{studioDetails?.studio_name || "BookInk Studio"}</h2>
                            <p className="text-[10px] text-white/70 mt-1">{studioDetails?.studio_phone || "CONTACT STUDIO"}</p>
                        </div>
                    </div>
                    <nav className="space-y-2 text-sm font-medium">
                        {[
                            { id: 'section-appointment', label: 'Home', icon: <Home size={18} />, action: () => document.getElementById('section-appointment')?.scrollIntoView({ behavior: 'smooth' }) },
                            { id: 'section-agreement', label: 'Customer Agreement', icon: <User size={18} />, action: () => document.getElementById('section-agreement')?.scrollIntoView({ behavior: 'smooth' }) },
                            { id: 'section-health', label: 'Health Conditions', icon: <MapPin size={18} />, action: () => document.getElementById('section-health')?.scrollIntoView({ behavior: 'smooth' }) },
                            { id: 'section-signature', label: 'Signature', icon: <PenTool size={18} />, action: () => document.getElementById('section-signature')?.scrollIntoView({ behavior: 'smooth' }) },
                        ].map(({ id, label, icon, action }) => (
                            <div
                                key={id}
                                onClick={action}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${
                                    activeSection === id
                                        ? 'bg-white/20 text-white font-bold shadow-inner'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                            >
                                <span className={activeSection === id ? 'text-white' : 'text-white/60'}>{icon}</span>
                                <span>{label}</span>
                                {activeSection === id && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white" />}
                            </div>
                        ))}
                    </nav>
                    <div className="mt-auto space-y-3">
                        <button
                            onClick={onBack}
                            className="w-full bg-red-500/20 hover:bg-red-500/40 text-white border border-red-400/40 hover:border-red-400/70 py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all shadow-lg active:scale-95 flex items-center justify-center gap-3"
                        >
                            <LogOut size={18} /> Exit Signing Mode
                        </button>
                        <p className="text-[10px] text-center text-white/40">© 2026 BOOKINK</p>
                    </div>
                </aside>
            )}

            <main className="flex-1 overflow-y-auto min-h-0 p-4 md:p-12 print:p-0 relative">
                {/* KIOSK MODE EXIT BUTTON */}
                {form?.isKiosk && (
                    <button
                        onClick={handleExitKiosk}
                        className="fixed top-6 right-6 z-50 bg-slate-800/80 backdrop-blur text-white px-4 py-2 rounded-full text-xs font-bold uppercase hover:bg-slate-900 transition shadow-lg flex items-center gap-2"
                    >
                        <LogOut size={14} /> Exit Kiosk
                    </button>
                )}

                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12 space-y-10 print:shadow-none">
                    <div className="text-center space-y-4">
                        <h1 className="text-3xl font-extrabold text-slate-900">{displayTitle}</h1>
                        <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mx-auto">{displayDesc}</p>
                    </div>

                    {/* INPUTS SECTION */}
                    <div id="section-appointment">
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">Appointment Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="w-full">
                                <label className="block text-xs font-bold text-slate-500 mb-1">Date</label>
                                <div className="border border-slate-200 rounded p-3 text-sm w-full bg-blue-50/50 text-blue-900 font-bold">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                            </div>
                            <div className="w-full">
                                <label className="block text-xs font-bold text-slate-500 mb-1">Artist <span className="text-red-500">*</span></label>
                                <select name="staff_name" onChange={handleInputChange} value={formData.staff_name} className={`border rounded p-3 text-sm w-full text-slate-600 outline-none ${fieldErrors.staff_name ? 'border-red-500 bg-red-50' : 'border-slate-300'}`}>
                                    <option value="">Select Appointment With...</option>
                                    {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="w-full">
                                <label className="block text-xs font-bold text-slate-500 mb-1">Price (£) <span className="text-red-500">*</span></label>
                                <input
                                    name="price"
                                    type="number"
                                    placeholder="0.00"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    className={`border rounded p-3 text-sm w-full text-slate-600 outline-none focus:border-blue-500 ${fieldErrors.price ? 'border-red-500 bg-red-50' : 'border-slate-300'}`}
                                />
                            </div>
                            {/* Bio field removed as per request */}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">Your Details</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <input name="first_name" value={formData.first_name} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.first_name ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="First Name *" />
                                <input name="middle_name" value={formData.middle_name} onChange={handleInputChange} className="border border-slate-300 rounded p-3 text-sm" placeholder="Middle Name" />
                                <input name="last_name" value={formData.last_name} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.last_name ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Surname *" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <input name="address" value={formData.address} onChange={handleInputChange} className={`col-span-2 border rounded p-3 text-sm ${fieldErrors.address ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Address *" />
                                <input name="postcode" value={formData.postcode} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.postcode ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Postcode *" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input name="phone" value={formData.phone} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.phone ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Phone Number *" />
                                <input name="email" value={formData.email} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Email Address *" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input name="dob" value={formData.dob} onChange={handleInputChange} type="date" className="border border-slate-300 rounded p-3 text-sm bg-blue-50/50 text-slate-500" />
                                <input name="age" value={formData.age} onChange={handleInputChange} className={`border rounded p-3 text-sm ${fieldErrors.age ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} placeholder="Age *" />
                            </div>
                        </div>
                    </div>

                    <div id="section-agreement">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-lg text-slate-800">Customer Agreement <span className="text-red-500">*</span></h3>
                            <button
                                onClick={() => {
                                    const agsToUse = displayAgreements.length > 0 ? displayAgreements : segments.filter(s => s.type === 'agreement');
                                    setSelectedAgreements(selectedAgreements.length >= agsToUse.length ? [] : [...agsToUse]);
                                }}
                                className="text-[11px] font-black text-white bg-blue-600 px-4 py-2 rounded-full uppercase tracking-tighter hover:bg-blue-700 shadow-md transition-all active:scale-95"
                            >
                                {selectedAgreements.length >= (displayAgreements.length > 0 ? displayAgreements.length : segments.filter(s => s.type === 'agreement').length) ? "Deselect Everything" : "Check All Terms"}
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(form?.agreements || []).map((ag, i) => {
                                const isString = typeof ag === 'string';
                                const text = isString ? ag : (ag.content || ag.title || 'Agreement');
                                const id = isString ? `ag-str-${i}` : ag.id;
                                const isChecked = isString
                                    ? selectedAgreements.includes(ag)
                                    : selectedAgreements.some(s => s.id === ag.id);

                                return (
                                    <label key={id} className="flex gap-4 items-start cursor-pointer group">
                                        <div className="w-6 h-6 border-2 border-slate-200 rounded-md flex items-center justify-center flex-shrink-0 bg-white group-hover:border-blue-400">
                                            <input
                                                type="checkbox"
                                                className="hidden peer"
                                                checked={isChecked}
                                                onChange={() => toggleAgreement(ag)}
                                            />
                                            <div className={`w-4 h-4 bg-blue-500 rounded-sm ${isChecked ? 'block' : 'hidden'}`}></div>
                                        </div>
                                        <p className="text-sm text-slate-600 leading-relaxed font-medium">{text}</p>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* ENHANCED HEALTH SCREENING */}
                    <div id="section-health">
                        <h3 className="font-bold text-lg text-slate-800 mb-2 border-b-2 border-red-400 w-12 pb-1">Health Screening</h3>
                        <p className="text-xs text-slate-500 mb-6">Please check all conditions that apply to you. This information is confidential and helps your artist provide safe service.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                            {(form?.health_conditions || []).map((cond, i) => {
                                const isString = typeof cond === 'string';
                                const label = isString ? cond : cond.content;
                                const isHighRisk = isString ? false : Boolean(cond.is_high_risk);
                                const id = isString ? `str-${i}` : cond.id;

                                return (
                                    <label key={id} className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-all ${selectedHealth.some(s => (isString ? s === cond : s.id === id)) ? (isHighRisk ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-300') : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedHealth.some(s => (isString ? s === cond : s.id === id)) ? (isHighRisk ? 'bg-red-500 border-red-500' : 'bg-blue-500 border-blue-500') : 'border-slate-300 bg-white'}`}>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={selectedHealth.some(s => (isString ? s === cond : s.id === id))}
                                                onChange={() => {
                                                    const item = isString ? cond : cond;
                                                    if (isString) {
                                                        setSelectedHealth(prev => prev.includes(item) ? prev.filter(s => s !== item) : [...prev, item]);
                                                    } else {
                                                        toggleHealth(item);
                                                    }
                                                }}
                                            />
                                            {selectedHealth.some(s => (isString ? s === cond : s.id === id)) && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                        </div>
                                        <span className={`text-sm font-medium ${isHighRisk ? 'text-red-700' : 'text-slate-700'}`}>{label}</span>
                                        {isHighRisk && <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold ml-auto">Requires Review</span>}
                                    </label>
                                );
                            })}
                            {/* Static "None of the above" for UX safety */}
                            <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-all ${selectedHealth.length === 0 ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200'}`}>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedHealth.length === 0 ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'}`}>
                                    {selectedHealth.length === 0 && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </div>
                                <span className="text-sm font-medium text-slate-700">None of the above</span>
                            </label>
                        </div>

                        {/* Conditional Details Box - appears when any condition is selected */}
                        {selectedHealth.length > 0 && !selectedHealth.includes('None of the above') && (
                            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-fade-in-up">
                                <label className="block text-sm font-bold text-amber-800 mb-2">
                                    ⚠️ Please provide more details for your artist <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={healthDetails}
                                    onChange={(e) => setHealthDetails(e.target.value)}
                                    placeholder="Describe your condition(s), any medications you take, and any precautions your artist should know about..."
                                    className="w-full border border-amber-300 rounded-lg p-3 text-sm min-h-[100px] focus:outline-none focus:border-amber-500 bg-white"
                                    required
                                />
                            </div>
                        )}
                    </div>

                    {/* LEGAL WAIVERS - Individual Acknowledgments */}
                    <div>
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">Legal Acknowledgments</h3>
                        <p className="text-xs text-slate-500 mb-6">You must acknowledge each statement below by checking the box.</p>
                        <div className="space-y-4">
                            {segments.filter(s => s.type === 'agreement').map((seg) => (
                                <label key={seg.id} className={`flex gap-4 items-start cursor-pointer p-4 rounded-xl border transition-all ${selectedAgreements.some(s => s.id === seg.id) ? 'bg-green-50 border-green-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                                    <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${selectedAgreements.some(s => s.id === seg.id) ? 'bg-green-500 border-group-500' : 'border-slate-300 bg-white'}`}>
                                        <input type="checkbox" className="hidden" checked={selectedAgreements.some(s => s.id === seg.id)} onChange={() => toggleAgreement(seg)} />
                                        {selectedAgreements.some(s => s.id === seg.id) && <Check size={14} className="text-white" />}
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-700 font-bold leading-relaxed uppercase tracking-tight mb-1">{seg.title}</p>
                                        <p className="text-sm text-slate-600 leading-relaxed font-medium whitespace-pre-wrap">{seg.content}</p>
                                    </div>
                                </label>
                            ))}

                            {/* No-Refund Policy with Dynamic Price - KEPT AS CORE FEATURE */}
                            <label className={`flex gap-4 items-start cursor-pointer p-4 rounded-xl border transition-all ${selectedAgreements.some(s => s.id === 'core_no_refund') ? 'bg-green-50 border-green-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                                <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${selectedAgreements.some(s => s.id === 'core_no_refund') ? 'bg-green-500 border-green-500' : 'border-slate-300 bg-white'}`}>
                                    <input type="checkbox" className="hidden" checked={selectedAgreements.some(s => s.id === 'core_no_refund')} onChange={() => toggleAgreement({ id: 'core_no_refund', title: 'No-Refund Policy', content: `I agree to the price of £${formData.price || '0.00'} and understand that all payments and deposits are non-refundable.` })} />
                                    {selectedAgreements.some(s => s.id === 'core_no_refund') && <Check size={14} className="text-white" />}
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 font-medium leading-relaxed"><strong>No-Refund Policy:</strong> I agree to the price of <span className="text-blue-600 font-bold">£{formData.price || '0.00'}</span> and understand that all payments and deposits are non-refundable.</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* MEDIA & MARKETING PERMISSIONS */}
                    <div>
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">Media Release</h3>
                        <div className={`p-4 rounded-xl border transition-all ${mediaConsent ? 'bg-purple-50 border-purple-300' : 'bg-white border-slate-200'}`}>
                            <div className="flex items-center gap-4 cursor-pointer" onClick={() => setMediaConsent(prev => !prev)}>
                                <div className={`w-14 h-8 rounded-full p-1 transition-all duration-200 flex-shrink-0 ${mediaConsent ? 'bg-green-500' : 'bg-slate-200 ring-1 ring-slate-300'}`}>
                                    <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${mediaConsent ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-700 font-medium">I grant <strong>{studioDetails?.name || 'the Studio'}</strong> and the Artist permission to use photos of my tattoo for social media and marketing purposes.</p>
                                </div>
                            </div>

                            {/* Conditional Instagram Handle Field */}
                            {mediaConsent && (
                                <div className="mt-4 animate-fade-in-up">
                                    <label className="block text-xs font-bold text-purple-700 mb-2">Instagram Handle (optional - we'll tag you!)</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-purple-500 font-bold">@</span>
                                        <input
                                            type="text"
                                            value={instagramHandle}
                                            onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                                            placeholder="your_username"
                                            className="flex-1 border border-purple-300 rounded-lg p-3 text-sm focus:outline-none focus:border-purple-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ID VERIFICATION */}
                    <div>
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">ID Verification</h3>
                        <div className="p-4 rounded-xl border border-slate-200 bg-white">
                            <p className="text-xs text-slate-500 mb-4">Please capture a photo of your valid ID (Driver's License, Passport, etc.)</p>
                            {idPhoto ? (
                                <div className="space-y-4">
                                    <div className="relative">
                                        <img src={idPhoto} alt="Client ID" className="w-full max-w-md rounded-lg border border-green-300" />
                                        <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                            <Check size={12} /> ID Captured
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIdPhoto(null)}
                                        className="text-sm text-red-500 font-medium hover:underline"
                                    >
                                        Retake Photo
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={async () => {
                                        try {
                                            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                                            setShowCamera(true);
                                            setTimeout(() => {
                                                if (videoRef.current) {
                                                    videoRef.current.srcObject = stream;
                                                    videoRef.current.play();
                                                }
                                            }, 100);
                                        } catch (err) {
                                            toast.error('Camera access denied. Please allow camera access to capture your ID.');
                                        }
                                    }}
                                    className="flex items-center gap-3 bg-slate-800 text-white px-6 py-4 rounded-xl font-bold text-sm hover:bg-slate-700 transition-all shadow-lg active:scale-95 touch-manipulation"
                                >
                                    <Camera size={20} /> Capture ID Photo
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Camera Modal */}
                    {showCamera && (
                        <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4">
                            <div className="w-full max-w-lg">
                                <video ref={videoRef} className="w-full rounded-lg" autoPlay playsInline></video>
                                <div className="flex gap-4 mt-4 justify-center">
                                    <button
                                        onClick={() => {
                                            const video = videoRef.current;
                                            const canvas = document.createElement('canvas');
                                            canvas.width = video.videoWidth;
                                            canvas.height = video.videoHeight;
                                            canvas.getContext('2d').drawImage(video, 0, 0);
                                            const photo = canvas.toDataURL('image/jpeg', 0.8);
                                            setIdPhoto(photo);
                                            video.srcObject.getTracks().forEach(track => track.stop());
                                            setShowCamera(false);
                                        }}
                                        className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg"
                                    >
                                        <Camera size={20} /> Capture
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (videoRef.current?.srcObject) {
                                                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                                            }
                                            setShowCamera(false);
                                        }}
                                        className="bg-slate-600 text-white px-8 py-4 rounded-xl font-bold text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div id="section-signature" className="break-inside-avoid">
                        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b-2 border-red-400 w-12 pb-1">Signature <span className="text-red-500">*</span></h3>
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-1">
                                <canvas ref={canvasRef} width={400} height={150}
                                    onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={endDrawing} onMouseLeave={endDrawing}
                                    onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={endDrawing}
                                    className="border-2 border-blue-500 rounded-lg w-full cursor-crosshair touch-none bg-white"></canvas>
                            </div>
                            <div className="flex flex-col gap-3 justify-end w-full md:w-48 print:hidden">
                                <button onClick={clearSignature} className="bg-slate-100 text-slate-600 px-6 py-2.5 rounded-full font-bold text-xs uppercase">Clear Signature</button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className={`bg-[#4F46E5] text-white px-6 py-2.5 rounded-full font-bold text-xs hover:bg-[#4338ca] transition w-full shadow-lg flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        "Submit Form"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

const ConsentFormDetailPage = ({ user, form, isSuperAdmin, onBack, onOpenPublicForm, onRefresh }) => {
    const [localForm, setLocalForm] = useState(form);
    const [showQR, setShowQR] = useState(false);

    // Picker State
    const [showPicker, setShowPicker] = useState(false);
    const [pickerType, setPickerType] = useState(null); // 'agreements' or 'health_conditions'
    const [availableAgreements, setAvailableAgreements] = useState([]);
    const [availableHealth, setAvailableHealth] = useState([]);
    const [selectedInPicker, setSelectedInPicker] = useState([]);
    const [quickAdd, setQuickAdd] = useState({ title: '', content: '' });

    useEffect(() => {
        if (!showPicker) {
            setSelectedInPicker([]);
            setQuickAdd({ title: '', content: '' });
        }
    }, [showPicker]);

    useEffect(() => { setLocalForm(form); }, [form]);

    useEffect(() => {
        // Fetch globals for the picker - filter by studio_id if not superadmin
        // Note: For now we fetch all and filter on frontend for simplicity, 
        // matching the refreshData pattern used elsewhere for superadmin flexibility.
        axios.get(`${API_URL}/agreements`).then(res => {
            const data = Array.isArray(res.data) ? res.data : [];
            if (isSuperAdmin) {
                setAvailableAgreements(data);
            } else {
                setAvailableAgreements(data.filter(a => a.studio_id === user.id));
            }
        }).catch(console.error);

        axios.get(`${API_URL}/health`).then(res => {
            const data = Array.isArray(res.data) ? res.data : [];
            if (isSuperAdmin) {
                setAvailableHealth(data);
            } else {
                setAvailableHealth(data.filter(h => h.studio_id === user.id));
            }
        }).catch(console.error);
    }, [isSuperAdmin, user.id]);

    useEffect(() => {
        if (!localForm) {
            // Give it 1 second to load, then go back if still null
            const timer = setTimeout(() => {
                if (!localForm) {
                    console.warn("Form data null, returning to list.");
                    onBack();
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [localForm, onBack]);

    if (!localForm) return (
        <div className="flex flex-col items-center justify-center p-8 sm:p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 font-medium text-center">Loading form details...</p>
        </div>
    );

    const handleDelete = async () => {
        if (window.confirm("Delete this form template?")) {
            try {
                await axios.delete(`${API_URL}/forms/${localForm.id}`);
                onBack();
            } catch (error) { toast.error("Failed to delete."); }
        }
    };
    const handleEditDescription = () => {
        const newDesc = prompt("Edit Top Notes:", localForm.description);
        if (newDesc !== null) updateFormInDb('description', newDesc);
    };

    // Updated Add Item to use Picker
    const handleInitiateAdd = (field) => {
        setPickerType(field);
        setShowPicker(true);
    };

    const handleConfirmAdd = (itemsToAdd) => {
        // itemsToAdd should be an array of strings
        if (itemsToAdd.length > 0) {
            updateFormInDb(pickerType, [...(localForm[pickerType] || []), ...itemsToAdd]);
        }
        setShowPicker(false);
    };

    const handleRemoveItem = (field, index) => {
        if (window.confirm("Remove this item?")) {
            const updatedList = localForm[field].filter((_, i) => i !== index);
            updateFormInDb(field, updatedList);
        }
    };
    const handleToggleSetting = (key) => {
        const newSettings = { ...localForm.settings, [key]: !localForm.settings[key] };
        updateFormInDb('settings', newSettings);
    };
    const handleQuickAdd = async () => {
        const title = quickAdd.title?.trim();
        const content = quickAdd.content?.trim();

        if (!title || !content) {
            toast.error("Both Title and Content are required to save a new section.");
            return;
        }

        try {
            const apiPath = pickerType === 'agreements' ? 'agreements' : 'health';
            const payload = {
                title,
                [pickerType === 'agreements' ? 'content' : 'questions']: content,
                studio_id: user.id
            };

            const res = await axios.post(`${API_URL}/${apiPath}`, payload);
            if (res.data.success) {
                // Refresh available lists
                const updated = await axios.get(`${API_URL}/${apiPath}`);
                if (pickerType === 'agreements') {
                    setAvailableAgreements(updated.data);
                } else {
                    setAvailableHealth(updated.data);
                }

                // Add to the local form automatically
                updateFormInDb(pickerType, [...(localForm[pickerType] || []), content]);

                // Reset quick add
                setQuickAdd({ title: '', content: '' });
                toast.success("Section saved to database and added to form!");
            } else {
                toast.error(res.data.message || "Failed to save section.");
            }
        } catch (e) {
                        toast.error(e.response?.data?.message || "Failed to save section. Please try again.");
        }
    };

    const updateFormInDb = (field, newData) => {
        const updatedForm = { ...localForm, [field]: newData };
        setLocalForm(updatedForm);

        // Validation: title is usually required for a form
        if (!updatedForm.title?.trim()) {
            console.warn("Form title is missing, skipping DB update to prevent corruption.");
            return;
        }

        axios.put(`${API_URL}/forms/${localForm.id}`, {
            title: updatedForm.title,
            description: updatedForm.description,
            agreements: updatedForm.agreements || [],
            health_conditions: updatedForm.health_conditions || [],
            settings: updatedForm.settings || {},
            studio_id: user.id
        }).then(res => {
            if (res.data.cloned && res.data.newId) {
                setLocalForm(prev => ({ ...prev, id: res.data.newId }));
                onRefresh(); // Refresh the list to show the newly owned form
            }
        }).catch(err => {
                        toast.error(err.response?.data?.message || "Failed to save changes. Is the server running?");
        });
    };



    const toggleInPicker = (item) => {
        if (selectedInPicker.includes(item)) {
            setSelectedInPicker(selectedInPicker.filter(i => i !== item));
        } else {
            setSelectedInPicker([...selectedInPicker, item]);
        }
    };

    return (
        <>
            {showQR && <QRCodeModal form={localForm} onClose={() => setShowQR(false)} customUrl={`https://consent.bookink.uk/?formId=${localForm.id}&studio_id=${user?.studio_id || user?.id}`} />}

            {/* Section Picker Modal */}
            {showPicker && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
                        <h3 className="text-xl font-bold text-slate-700 mb-4">
                            Select {pickerType === 'agreements' ? 'Agreement' : 'Health'} Sections
                        </h3>
                        <div className="max-h-60 overflow-y-auto space-y-2 mb-6 border p-2 rounded bg-slate-50 shadow-inner">
                            {pickerType === 'agreements' && availableAgreements.map(a => (
                                <div key={a.id}
                                    className={`p-3 bg-white border rounded cursor-pointer transition flex items-start gap-3 ${selectedInPicker.includes(a.content) ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 hover:border-blue-300'}`}
                                    onClick={() => toggleInPicker(a.content)}>
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 flex-shrink-0 ${selectedInPicker.includes(a.content) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300'}`}>
                                        {selectedInPicker.includes(a.content) && <Check size={14} />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-bold text-sm text-slate-800">{a.title}</div>
                                        <div className="text-xs text-slate-500 line-clamp-2">{a.content}</div>
                                    </div>
                                </div>
                            ))}
                            {pickerType === 'health_conditions' && availableHealth.map(h => (
                                <div key={h.id}
                                    className={`p-3 bg-white border rounded cursor-pointer transition flex items-start gap-3 ${selectedInPicker.includes(h.questions) ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 hover:border-blue-300'}`}
                                    onClick={() => toggleInPicker(h.questions)}>
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 flex-shrink-0 ${selectedInPicker.includes(h.questions) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300'}`}>
                                        {selectedInPicker.includes(h.questions) && <Check size={14} />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-bold text-sm text-slate-800">{h.title}</div>
                                        <div className="text-xs text-slate-500 line-clamp-2">{h.questions}</div>
                                    </div>
                                </div>
                            ))}
                            {((pickerType === 'agreements' && availableAgreements.length === 0) || (pickerType === 'health_conditions' && availableHealth.length === 0)) && (
                                <div className="text-center text-slate-400 text-sm py-8 italic bg-white border border-dashed rounded">
                                    No sections found in repository.
                                </div>
                            )}
                        </div>

                        {/* QUICK ADD NEW SECTION */}
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Or create & add new section</h4>
                            <div className="space-y-2">
                                <input
                                    className="w-full border border-slate-200 rounded p-2 text-sm focus:border-blue-500 outline-none transition"
                                    placeholder="Section Title (e.g. Health Risks)"
                                    value={quickAdd.title}
                                    onChange={e => setQuickAdd({ ...quickAdd, title: e.target.value })}
                                />
                                <textarea
                                    className="w-full border border-slate-200 rounded p-2 text-sm focus:border-blue-500 outline-none transition h-20"
                                    placeholder={pickerType === 'agreements' ? "Agreement Content..." : "Health Questions..."}
                                    value={quickAdd.content}
                                    onChange={e => setQuickAdd({ ...quickAdd, content: e.target.value })}
                                />
                                <button
                                    onClick={handleQuickAdd}
                                    className="w-full bg-white border border-blue-200 text-[#5C5CFF] font-bold py-2 rounded text-xs uppercase hover:bg-blue-50 transition"
                                >
                                    + SAVE TO REPOSITORY & ADD TO FORM
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                            <button onClick={() => setShowPicker(false)} className="text-xs font-bold text-slate-400 hover:text-red-500 uppercase tracking-tighter">Discard Changes</button>
                            <div className="flex gap-2">
                                <button onClick={() => setShowPicker(false)} className="bg-slate-100 text-slate-500 px-5 py-2 rounded text-sm font-bold uppercase transition hover:bg-slate-200">Cancel</button>
                                <button onClick={() => handleConfirmAdd(selectedInPicker)} className="bg-[#5C5CFF] text-white px-8 py-2 rounded text-sm font-bold uppercase transition hover:bg-[#4b4be6] shadow-md">Add Selected ({selectedInPicker.length})</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6 animate-fade-in-up">
                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm print:hidden">
                    <div className="mb-4">
                        <button onClick={onBack} className="text-slate-400 hover:text-slate-600 flex items-center gap-1 font-bold text-sm transition-colors">
                            <ChevronRight className="rotate-180" size={16} /> Back to Forms
                        </button>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-700 mb-1">Tattoo Consent Form</h2>
                            <div className="text-xs text-slate-400 font-mono">Form ID: #PFC{localForm.id}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <a href={`/?formId=${localForm.id}&studio_id=${user?.studio_id || user?.id}`} target="_blank" rel="noopener noreferrer" className="bg-[#5C5CFF] text-white px-6 py-2 rounded-md font-bold text-xs uppercase shadow-sm w-full hover:bg-[#4b4be6] block text-center">VIEW CONSENT FORM</a>
                            <button onClick={() => setShowQR(true)} className="bg-[#5C5CFF] text-white px-6 py-2 rounded-md font-bold text-xs uppercase shadow-sm w-full hover:bg-[#4b4be6] flex items-center justify-center gap-2"><QrCode size={14} /> PRINT QR PAGE</button>
                            <button onClick={handleDelete} className="bg-red-500 text-white px-6 py-2 rounded-md font-bold text-xs uppercase shadow-sm w-full flex items-center justify-center gap-2 hover:bg-red-600"><Trash2 size={14} /> DELETE CONSENT FORM</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
                        <div className="lg:col-span-3 space-y-8">
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-bold text-slate-700">Studio Default Form - {localForm.title}</h3>
                                    <button onClick={handleEditDescription} className="bg-[#5C5CFF] text-white px-3 py-1 rounded text-xs font-bold uppercase hover:bg-[#4b4be6]">EDIT TOP NOTES</button>
                                </div>
                                <p className="text-sm text-slate-500 leading-relaxed border-t border-slate-100 pt-4">{localForm.description}</p>
                            </div>

                            {/* Customer Agreement */}
                            <div>
                                <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-2">
                                    <h3 className="font-bold text-slate-800 text-lg uppercase tracking-tight">Customer Agreement</h3>
                                    <button onClick={() => handleInitiateAdd('agreements')} className="bg-[#EBEBFF] text-[#5C5CFF] px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-[#dadaff]">+ ADD ITEM</button>
                                </div>
                                <div className="space-y-3">
                                    {localForm.agreements?.map((ag, i) => {
                                        const text = typeof ag === 'string' ? ag : (ag.content || ag.title || 'Agreement');
                                        return (
                                            <div key={i} className="flex gap-4 group items-start p-2 rounded hover:bg-slate-50 transition">
                                                <div className="w-5 h-5 border-2 border-slate-300 rounded flex-shrink-0 mt-0.5 bg-white"></div>
                                                <p className="text-sm text-slate-600 leading-relaxed flex-1">{text}</p>
                                                <button onClick={() => handleRemoveItem('agreements', i)} className="text-red-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"><Trash2 size={16} /></button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Health Conditions */}
                            <div>
                                <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-2">
                                    <h3 className="font-bold text-slate-800 text-lg uppercase tracking-tight">Health Conditions</h3>
                                    <button onClick={() => handleInitiateAdd('health_conditions')} className="bg-[#EBEBFF] text-[#5C5CFF] px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-[#dadaff]">+ ADD ITEM</button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {localForm.health_conditions?.map((hc, i) => {
                                        const text = typeof hc === 'string' ? hc : (hc.content || hc.title || 'Condition');
                                        return (
                                            <div key={i} className="bg-slate-100 px-3 py-2 rounded text-xs text-slate-600 font-bold flex items-center justify-between group">
                                                <span className="flex items-center gap-2 text-left"><X size={14} className="text-slate-400 flex-shrink-0" /> {text}</span>
                                                <button onClick={() => handleRemoveItem('health_conditions', i)} className="text-red-300 hover:text-red-500 hidden group-hover:block ml-2"><Trash2 size={12} /></button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Right Settings */}
                        <div className="lg:col-span-1 border-l border-slate-100 pl-6 space-y-6">
                            <div className="space-y-4">
                                <ToggleItem label="Show Upload Photo ID" active={localForm.settings?.show_photo} onClick={() => handleToggleSetting('show_photo')} />
                                <ToggleItem label="Show Signature" active={localForm.settings?.show_signature} onClick={() => handleToggleSetting('show_signature')} />
                            </div>
                            <div className="border-t border-slate-100 pt-4 space-y-4">
                                <ToggleItem label="Email To Customer" active={localForm.settings?.email_customer} onClick={() => handleToggleSetting('email_customer')} />
                                <ToggleItem label="Email To Artist" active={false} />
                                <ToggleItem label="Email To Studio" active={false} />
                            </div>
                            <button onClick={onBack} className="w-full text-slate-400 text-xs font-bold hover:text-slate-600 mt-8 flex items-center justify-center gap-1 uppercase tracking-tighter"><ChevronRight className="rotate-180" size={14} /> BACK TO LIST</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const ConsentFormsListPage = ({ forms, onCreate, onView, onSend, onRefresh }) => {
    // Independent Delete for List View items
    const handleDeleteFromList = async (e, id) => {
        e.stopPropagation();
        if (window.confirm("Delete this template?")) {
            try {
                await axios.delete(`${API_URL}/forms/${id}`);
                onRefresh();
            } catch (err) { toast.error("Error deleting."); }
        }
    };

    return (
        <div className="space-y-6 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-700 flex items-center gap-2"><span className="hidden sm:inline text-slate-400 font-normal">Home /</span> List Consent Forms</h2>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">Manage your digital consent form templates.</p>
                </div>
                <button onClick={onCreate} className="w-full sm:w-auto bg-[#5C5CFF] text-white px-6 py-2.5 rounded-md font-bold text-sm shadow-sm hover:bg-[#4b4be6]">CREATE FORM</button>
            </div>

            <div className="space-y-4">
                {forms.map(form => (
                    <div key={form.id} className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm hover:shadow-md transition cursor-pointer" onClick={() => onView(form)}>
                        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                            <div className="flex-1 w-full">
                                <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-lg font-bold text-slate-700">{form.title}</h3>
                                    {form.is_default === 1 && <span className="bg-slate-800 text-white text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">Default</span>}
                                </div>
                                <p className="text-sm text-slate-500 max-w-3xl mb-4 line-clamp-2 leading-relaxed">{form.description}</p>
                                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(form.created_at).toLocaleDateString()}</span>
                                    <span className="flex items-center gap-1.5"><FileText size={14} /> {form.agreements?.length || 0} Agreements</span>
                                    <span className="flex items-center gap-1.5"><FilePlus size={14} /> {form.health_conditions?.length || 0} Questions</span>
                                    {form.settings?.show_signature ? <span className="flex items-center gap-1.5 text-green-600 font-bold"><CheckSquare size={14} /> Signature</span> : <span className="flex items-center gap-1.5 text-red-500 font-bold"><X size={14} /> Signature</span>}
                                </div>
                            </div>
                            <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-3 w-full md:w-auto pt-4 md:pt-0 border-t md:border-none border-slate-100">
                                <div className="flex items-center gap-2">
                                    <button onClick={(e) => { e.stopPropagation(); onSend(form); }} className="bg-[#666CFF] text-white px-4 py-2 rounded-md text-[10px] font-bold uppercase shadow-sm hover:bg-[#555BDD] flex items-center gap-2">
                                        <Send size={12} /> SEND FORM
                                    </button>
                                    <button onClick={(e) => handleDeleteFromList(e, form.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"><Trash2 size={16} /></button>
                                </div>
                                <button className="text-[#5C5CFF] hover:bg-indigo-50 px-2 py-2 rounded-md text-[10px] font-bold uppercase transition flex items-center gap-1">
                                    VIEW <ChevronRight size={14} className="opacity-50" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {forms.length === 0 && <div className="text-center p-6 sm:p-12 text-slate-400 bg-white rounded-lg border border-dashed border-slate-300">No forms found. Create one to get started.</div>}
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------
// PENDING STUDIOS PAGE (SUPERADMIN ONLY)
// ----------------------------------------------------------------------
// PendingStudiosPage removed (consolidated into SuperAdminDashboard)

// CustomerListPage (StudioManagementList) removed (consolidated into SuperAdminDashboard)

// --- PAGE: CUSTOMER DETAILS (EDITABLE PROFILE - IMAGE 2) ---
const CustomerDetailsPage = ({ client, onBack, onRefresh, onChangePage = () => { } }) => {
    const [form, setForm] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [isDirty, setIsDirty] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (client) {
            setForm({
                ...client,
                dob: client.dob ? client.dob.split('T')[0] : '',
                photo: client.photo || ''
            });
        }
    }, [client]);

    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setIsDirty(true);
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 800 * 1024) { toast.error("File too large. Max 800KB."); return; }
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm(prev => ({ ...prev, photo: reader.result }));
                setIsDirty(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        try {
            await axios.put(`${API_URL}/clients/${client.id}`, form);
            toast.success("Customer details updated successfully!");
            setIsDirty(false);
            if (onRefresh) onRefresh();
        } catch (error) {
                        toast.error(`Failed to update customer: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleDeleteClient = async () => {
        if (window.confirm("Are you sure you want to PERMANENTLY DELETE this customer account? This cannot be undone.")) {
            try {
                await axios.delete(`${API_URL}/clients/${client.id}`);
                toast.success("Customer account deleted.");
                if (onRefresh) onRefresh();
                onBack();
            } catch (e) {
                                toast.error("Delete failed.");
            }
        }
    };

    const triggerFileUpload = () => fileInputRef.current.click();

    useEffect(() => {
        if (!form) {
            const timer = setTimeout(() => {
                if (!form) onBack();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [form, onBack]);

    if (!form) return (
        <div className="flex flex-col items-center justify-center p-8 sm:p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 font-medium text-center">Loading customer data...</p>
        </div>
    );

    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            <h2 className="text-xl md:text-2xl font-bold text-slate-700">Customer Details</h2>
            <div className="bg-white p-5 sm:p-8 rounded-lg border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    {/* Left: Avatar & Actions */}
                    <div className="flex flex-col items-center gap-4 min-w-[160px]">
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 shadow-inner">
                            {form.photo ? (
                                <img src={form.photo} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-3xl md:text-4xl font-bold text-slate-400 uppercase">{getInitials(form.first_name + ' ' + form.last_name)}</span>
                            )}
                        </div>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/png, image/gif, image/jpeg" onChange={handlePhotoUpload} />

                        <div className="flex flex-col w-full gap-2">
                            <button onClick={triggerFileUpload} className="bg-[#5C5CFF] text-white py-2.5 rounded text-[10px] font-bold uppercase hover:bg-[#4b4be6] shadow-sm">Upload Photo</button>
                            <div className="flex gap-2">
                                <button onClick={() => setForm(prev => ({ ...prev, photo: '' }))} className="flex-1 border border-slate-200 text-slate-500 py-2 rounded text-[10px] font-bold uppercase hover:bg-slate-50 transition">Reset</button>
                            </div>
                        </div>
                        <div className="text-[10px] text-slate-400 text-center leading-tight">Max size 800KB</div>
                    </div>

                    {/* Right: Form */}
                    <div className="flex-1 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Input label="First Name" name="first_name" value={form.first_name} onChange={handleInputChange} />
                            <Input label="Middle Name" name="middle_name" value={form.middle_name} onChange={handleInputChange} />
                            <Input label="Last Name" name="last_name" value={form.last_name} onChange={handleInputChange} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input label="Address" name="address" value={form.address} onChange={handleInputChange} />
                            <Input label="Postcode" name="postcode" value={form.postcode} onChange={handleInputChange} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input label="Phone Number" name="phone" value={form.phone} onChange={handleInputChange} />
                            <Input label="Email Address" name="email" value={form.email} onChange={handleInputChange} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input label="Date of Birth" name="dob" type="date" value={form.dob} onChange={handleInputChange} />
                            <Input label="Age" name="age" value={form.age} onChange={handleInputChange} />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-slate-100">
                            <button onClick={handleSave} className="order-1 sm:order-2 bg-[#5C5CFF] text-white px-8 py-3 rounded-md font-bold text-xs uppercase shadow-md hover:bg-[#4b4be6] w-full sm:w-auto">SAVE CHANGES</button>
                            <button onClick={onBack} className="order-2 sm:order-1 border border-slate-300 text-slate-500 px-8 py-3 rounded-md font-bold text-xs uppercase hover:bg-slate-50 w-full sm:w-auto">CANCEL</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Consent Forms History Section */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-slate-700">Consent Forms History</h3>
                </div>
                <div className="p-6 space-y-6">
                    <div className="bg-slate-50 p-4 rounded-lg flex flex-col sm:flex-row flex-wrap gap-4 sm:justify-between sm:items-center text-sm text-slate-600 border border-slate-100">
                        <div className="flex-1 min-w-[200px]">
                            <div className="font-bold text-slate-800">{form.first_name} {form.last_name}</div>
                            <span className="text-[10px] text-blue-500 font-mono font-bold block mt-0.5 uppercase tracking-tighter">Ref: #{form.consent_id || 'N/A'}</span>
                            <span className="text-xs text-slate-400 font-normal italic">Signed: {form.appt_date ? new Date(form.appt_date).toLocaleDateString('en-GB') : '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-medium">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold ${getColorClass(form.staff_name || 'Unassigned')}`}>
                                {getInitials(form.staff_name || 'Unassigned')}
                            </div>
                            {form.staff_name || 'Unassigned'}
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-none pt-3 sm:pt-0 mt-2 sm:mt-0">
                            <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border border-green-200">Signed</span>
                            <div className="text-[#666CFF] font-black">£{form.price || '0.00'}</div>
                        </div>
                        <button
                            onClick={() => (onChangePage && typeof onChangePage === 'function') ? onChangePage('sentFormDetails') : onBack()}
                            className="w-full sm:w-auto bg-[#5C5CFF] text-white px-6 py-2 rounded-lg text-[10px] font-bold uppercase shadow-sm hover:bg-[#4b4be6] mt-2 sm:mt-0"
                        >
                            View Record
                        </button>
                    </div>

                    {/* Signed Agreements in CRM View */}
                    <div className="pt-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Agreements Accepted</h4>
                        <div className="grid grid-cols-1 gap-3">
                            {(() => {
                                const rawAgreements = form.agreements;
                                let agreements = [];
                                if (Array.isArray(rawAgreements)) {
                                    agreements = rawAgreements;
                                } else if (typeof rawAgreements === 'string') {
                                    try {
                                        agreements = JSON.parse(rawAgreements);
                                        if (!Array.isArray(agreements)) agreements = [agreements];
                                    } catch (e) {
                                        if (rawAgreements.trim().length > 0) agreements = [rawAgreements];
                                    }
                                }

                                return agreements.length > 0 ? (
                                    agreements.map((text, i) => (
                                        <div key={i} className="flex gap-4 items-start bg-slate-50/30 p-4 rounded-lg border border-slate-100">
                                            <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                                                <Check size={12} strokeWidth={4} />
                                            </div>
                                            <p className="text-xs text-slate-700 leading-relaxed font-bold">{text}</p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-sm text-slate-500 italic p-4 bg-slate-50 rounded border border-dashed border-slate-200">
                                        No specific agreement text recorded for this session. (Selected: 0)
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Health Conditions in CRM View */}
                    <div className="pt-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Confirmed Health Conditions</h4>
                        <div className="flex flex-wrap gap-2">
                            {form.health_conditions && form.health_conditions.length > 0 ? (
                                form.health_conditions.map((cond, i) => (
                                    <div key={i} className="bg-green-50 px-3 py-2 rounded border border-green-200 text-[10px] font-bold text-slate-700 flex items-center gap-2 shadow-sm">
                                        <Check size={12} className="text-green-600" strokeWidth={3} /> {cond}
                                    </div>
                                ))
                            ) : (
                                <div className="text-xs text-slate-400 italic">No health conditions selected.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Account */}
            <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-700">Delete Customer Account</h3>
                <div className="bg-orange-50 border border-orange-100 p-4 rounded text-orange-800 text-sm">
                    <strong>Are you sure you want to delete this customer's account?</strong><br />
                    Once you delete the account, there is no going back. Please be certain.
                </div>
                <button onClick={handleDeleteClient} className="bg-red-500 text-white px-6 py-3 rounded-md font-bold text-xs uppercase hover:bg-red-600 shadow-sm">DEACTIVATE ACCOUNT</button>
            </div>
        </div>
    );
};

// --- PAGE: SENT FORM DETAILS (SIGNED VIEW - IMAGE 3 - Renamed from ClientDetailsPage) ---
const SentFormDetailPage = ({ client, studios, onBack, onRefresh }) => {
    // Fetch ALL health conditions for this studio so we can show tick/cross
    const [allHealthConditions, setAllHealthConditions] = React.useState([]);
    React.useEffect(() => {
        if (client?.studio_id) {
            axios.get(`${API_URL}/health?studio_id=${client.studio_id}`)
                .then(res => setAllHealthConditions(Array.isArray(res.data) ? res.data : []))
                .catch(() => setAllHealthConditions([]));
        }
    }, [client?.studio_id]);

    useEffect(() => {
        if (!client) {
            const timer = setTimeout(() => {
                if (!client) onBack();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [client, onBack]);

    if (!client) return (
        <div className="flex flex-col items-center justify-center p-8 sm:p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 font-medium text-center">Loading record details...</p>
        </div>
    );
    const clientConditions = client.health_conditions && Array.isArray(client.health_conditions) ? client.health_conditions : [];

    const handleDeleteClient = async () => {
        if (window.confirm("Are you sure you want to PERMANENTLY DELETE this signed consent form? This cannot be undone.")) {
            try {
                await axios.delete(`${API_URL}/clients/${client.id}`);
                toast.success("Client record deleted.");
                onRefresh(); // Refresh list data
                onBack(); // Go back to list
            } catch (e) {
                                const msg = e.response?.data?.message || "Delete failed. Please try again.";
                toast.error(`Error: ${msg}`);
            }
        }
    };

    const handleResendEmail = async () => {
        try {
            const res = await axios.post(`${API_URL}/clients/${client.id}/email`);
            toast.success(res.data.message || "Email resent successfully!");
        } catch (error) {
            toast.error("Failed to resend email.");
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleDownloadPDF = () => {
        const thisStudio = studios.find(s => s.id === client.studio_id) || { studio_name: 'BookInk Studio' };
        const doc = generateConsentPDF(client, thisStudio);
        doc.save(`Consent_Form_${client.first_name}_${client.last_name}.pdf`);
    };


    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in-up font-sans print:p-0 print:m-0 print:max-w-none">

            {/* Header only for print */}
            <div className="hidden print:block text-center mb-12 border-b pb-6">
                <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tight mb-2">{(studios.find(s => s.id === client.studio_id) || {}).studio_name || 'The Projects Tattoo Studio'}</h1>
                <p className="text-base text-slate-600 font-medium">{(studios.find(s => s.id === client.studio_id) || {}).studio_phone || '122 Cornwall St, Plymouth, PL1 1NF'}</p>
                <div className="mt-4 inline-block bg-slate-900 text-white px-4 py-1 text-[10px] font-bold uppercase tracking-widest">Signed Consent Form Record</div>
            </div>

            {/* Breadcrumb Navigation - Hidden on Print */}
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-4 print:hidden">
                <button onClick={onBack} className="hover:text-slate-600 flex items-center gap-1 font-bold">
                    <ChevronRight className="rotate-180" size={16} /> Back to List
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start print:block">

                {/* LEFT COLUMN: MAIN DOCUMENT (75% Width) */}
                <div className="lg:col-span-3 space-y-6 print:w-full">
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 print:shadow-none print:border-none print:p-0">

                        {/* 1. NEW COMPACT HEADER & SIGNATURE */}
                        <div className="flex flex-col md:flex-row border border-slate-100 rounded-lg overflow-hidden mb-8 shadow-sm print:shadow-none">
                            <div className="w-full md:w-1/2 p-4 border-b md:border-b-0 md:border-r border-slate-100">
                                <div className="text-[10px] text-slate-400 uppercase font-black mb-2 tracking-widest">Customer's Signature</div>
                                <div className="h-28 flex items-center justify-center border border-slate-100 rounded p-4 bg-slate-50/30">
                                    {client.signature ?
                                        <img src={client.signature} alt="Signature" className="max-h-full max-w-full object-contain" /> :
                                        <span className="text-3xl font-cursive text-slate-300 italic">{client.first_name} {client.last_name}</span>
                                    }
                                </div>
                            </div>
                            <div className="w-full md:w-1/2 p-6 flex flex-col justify-center items-center md:items-end text-center md:text-right space-y-1 bg-white">
                                <div className="text-lg sm:text-xl font-black text-slate-700 tracking-tight">CONSENT ID: #{client.consent_id || 'MCNWREYJ83'}</div>
                                <div className="text-[10px] sm:text-[11px] text-slate-400 font-bold uppercase tracking-tight sm:tracking-normal">
                                    Date Signed: {client.appt_date ? new Date(client.appt_date).toLocaleDateString('en-GB') : 'N/A'} - {client.appt_date && new Date(client.appt_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </div>
                                <div className="text-[10px] sm:text-[11px] text-slate-400 font-bold uppercase tracking-tight sm:tracking-normal">IP Address: {client.ip_address || '92.40.213.37'}</div>
                            </div>
                        </div>

                        {/* Print Only Summary */}
                        <div className="hidden print:grid grid-cols-2 border-y border-slate-200 py-6 mb-8">
                            <div>
                                <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Service Fee</div>
                                <div className="text-2xl font-black text-[#5C5CFF]">£{client.price || '0.00'}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Status</div>
                                <div className="text-sm font-bold text-green-600 uppercase">Electronically Signed & Verified</div>
                            </div>
                        </div>

                        {/* 2. PURPLE APPOINTMENT BAR */}
                        <div className="bg-[#F3F3FF] rounded-lg p-6 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-10 border border-[#EBEBFF]">
                            <div>
                                <div className="text-[#7C7CFF] text-[10px] font-black uppercase mb-1 tracking-wider">Appointment Date</div>
                                <div className="text-[#5C5CFF] font-bold text-base">
                                    {client.appt_date ? new Date(client.appt_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                </div>
                            </div>
                            <div>
                                <div className="text-[#7C7CFF] text-[10px] font-black uppercase mb-1 tracking-wider">Appointment With</div>
                                <div className="text-[#5C5CFF] font-bold text-base">{client.staff_name || 'Unassigned'}</div>
                            </div>
                        </div>

                        {/* 3. UNDERLINED DETAILS GRID */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-10 gap-x-12 mb-12">
                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">First Name</label>
                                <div className="text-sm text-slate-600 font-bold">{client.first_name}</div>
                            </div>
                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Middle Name</label>
                                <div className="text-sm text-slate-600 font-bold">{client.middle_name || '-'}</div>
                            </div>
                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Surname</label>
                                <div className="text-sm text-slate-600 font-bold">{client.last_name}</div>
                            </div>

                            <div className="sm:col-span-2 md:col-span-2 border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Address</label>
                                <div className="text-sm text-slate-600 font-bold">{client.address || '-'}</div>
                            </div>
                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Postcode</label>
                                <div className="text-sm text-slate-600 font-bold">{client.postcode || '-'}</div>
                            </div>

                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Phone Number</label>
                                <div className="text-sm text-slate-600 font-bold">{client.phone}</div>
                            </div>
                            <div className="sm:col-span-2 md:col-span-2 border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Email Address</label>
                                <div className="text-sm text-slate-600 font-bold">{client.email}</div>
                            </div>

                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Date of Birth</label>
                                <div className="text-sm text-slate-600 font-bold">{client.dob ? new Date(client.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                            </div>
                            <div className="border-b border-slate-100 pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Age</label>
                                <div className="text-sm text-slate-600 font-bold">{client.age || '-'}</div>
                            </div>
                            <div className="pb-1">
                                <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-tight">Service Fee</label>
                                <div className="text-sm text-[#5C5CFF] font-black">£{client.price || '0.00'}</div>
                            </div>
                        </div>

                        {/* 4. CUSTOMER AGREEMENT - MOCKED STYLE */}
                        <div className="mb-12 page-break-inside-avoid">
                            <h3 className="text-2xl font-bold text-slate-600 mb-8">Customer Agreement</h3>
                            <div className="space-y-6">
                                {(() => {
                                    const rawAgreements = client.agreements;
                                    let agreements = [];
                                    if (Array.isArray(rawAgreements)) {
                                        agreements = rawAgreements;
                                    } else if (typeof rawAgreements === 'string') {
                                        try {
                                            agreements = JSON.parse(rawAgreements);
                                            if (!Array.isArray(agreements)) agreements = [agreements];
                                        } catch (e) {
                                            if (rawAgreements.trim().length > 0) agreements = [rawAgreements];
                                        }
                                    }

                                    return agreements.length > 0 ? (
                                        agreements.map((item, i) => {
                                            const isString = typeof item === 'string';
                                            const text = isString ? item : (item.content || item.title || 'Agreement');
                                            return (
                                                <div key={i} className="flex gap-6 items-start pb-4 border-b border-slate-50 last:border-0">
                                                    <div className="flex-shrink-0 mt-1">
                                                        <div className="w-6 h-6 bg-[#C8FFC8] rounded flex items-center justify-center">
                                                            <Check size={14} className="text-[#32CD32]" strokeWidth={4} />
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-slate-700 leading-relaxed font-bold">{text}</p>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-sm text-slate-500 italic bg-slate-50 p-4 rounded border border-dashed border-slate-200">
                                            No agreements were recorded for this session. (Selected: 0)
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* 5. HEALTH CONDITIONS - FULL LIST WITH TICK/CROSS */}
                        <div className="page-break-inside-avoid">
                            <h3 className="text-2xl font-bold text-slate-600 mb-8">Health Conditions</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {allHealthConditions.length > 0 ? allHealthConditions.map((cond, i) => {
                                    const condText = typeof cond === 'object' ? (cond.questions || cond.title || 'Condition') : cond;
                                    const isSelected = clientConditions.some(cc => {
                                        const ccText = typeof cc === 'object' ? (cc.content || cc.title || '') : cc;
                                        return ccText === condText;
                                    });
                                    return (
                                        <div key={i} className={`px-4 py-3 rounded text-[11px] flex items-center gap-3 font-bold border ${isSelected
                                            ? 'bg-green-50 text-slate-700 border-green-200'
                                            : 'bg-[#EDF2F7] text-slate-400 border-slate-200'
                                            }`}>
                                            {isSelected
                                                ? <Check size={12} className="text-green-600" strokeWidth={3} />
                                                : <X size={12} className="text-slate-400" strokeWidth={3} />
                                            }
                                            {condText}
                                        </div>
                                    );
                                }) : clientConditions.length > 0 ? clientConditions.map((cond, i) => {
                                    const text = typeof cond === 'object' ? (cond.content || cond.title || 'Condition') : cond;
                                    return (
                                        <div key={i} className="bg-green-50 px-4 py-3 rounded text-[11px] text-slate-700 flex items-center gap-3 font-bold border border-green-200">
                                            <Check size={12} className="text-green-600" strokeWidth={3} /> {text}
                                        </div>
                                    );
                                }) : (
                                    <div className="text-sm text-slate-400 italic">No health conditions declared.</div>
                                )}
                            </div>
                        </div>

                    </div>

                        {/* 6. MEDIA CONSENT & ID PHOTO */}
                        {(client.media_consent !== undefined || client.id_photo || client.instagram_handle) && (
                            <div className="mt-8 page-break-inside-avoid">
                                <h3 className="text-2xl font-bold text-slate-600 mb-4">Media & ID</h3>
                                <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${client.media_consent ? 'bg-green-500' : 'bg-slate-300'}`}>
                                        {client.media_consent
                                            ? <Check size={14} className="text-white" />
                                            : <X size={14} className="text-white" />
                                        }
                                    </div>
                                    <p className="text-sm font-medium text-slate-700">
                                        Media Release: <span className={client.media_consent ? 'text-green-600 font-bold' : 'text-slate-400 font-bold'}>{client.media_consent ? 'Consented' : 'Not consented'}</span>
                                    </p>
                                    {client.instagram_handle && (
                                        <span className="ml-auto text-sm text-purple-600 font-bold flex items-center gap-1">
                                            <Instagram size={14} /> @{client.instagram_handle}
                                        </span>
                                    )}
                                </div>
                                {client.id_photo && (
                                    <div className="mt-4">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-widest">ID Document</div>
                                        <img src={client.id_photo} alt="Client ID" className="max-w-xs sm:max-w-sm rounded-lg border border-slate-200 shadow-sm" />
                                    </div>
                                )}
                            </div>
                        )}

                    <div className="text-center text-xs text-slate-400 pb-8 mt-8 border-t pt-8 print:hidden">
                        © 2026 - Made with ❤️ by <span className="font-bold text-slate-600">BOOKINK</span>
                    </div>
                </div>

                {/* RIGHT COLUMN: STICKY ACTIONS (25% Width) - Hidden on Print */}
                <div className="lg:col-span-1 space-y-4 print:hidden">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 space-y-3 sticky top-6">
                        <button onClick={() => {
                            const thisStudio = studios.find(s => s.id === client.studio_id) || { studio_name: 'BookInk Studio' };
                            const doc = generateConsentPDF(client, thisStudio);
                            window.open(doc.output('bloburl'), '_blank');
                        }} className="w-full bg-[#666CFF] hover:bg-[#5C5CFF] text-white py-3 rounded-md text-[10px] font-bold uppercase shadow-sm transition flex items-center justify-center gap-2 tracking-wide">
                            <Eye size={14} /> VIEW CONSENT FORM
                        </button>
                        <button onClick={handleResendEmail} className="w-full bg-[#5C5CFF] hover:bg-[#4b4be6] text-white py-3 rounded-md text-[10px] font-bold uppercase shadow-sm transition flex items-center justify-center gap-2 tracking-wide">
                            RESEND COPY OF CONSENT
                        </button>
                        <button onClick={() => window.print()} className="w-full bg-white border border-slate-300 text-slate-500 hover:bg-slate-50 py-3 rounded-md text-[10px] font-bold uppercase transition flex items-center justify-center gap-2 tracking-wide">
                            PRINT
                        </button>
                        <button onClick={handleDeleteClient} className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-md text-[10px] font-bold uppercase shadow-sm transition flex items-center justify-center gap-2 tracking-wide">
                            <Trash2 size={14} /> DELETE CONSENT FORM
                        </button>

                    </div>
                </div>

            </div>
        </div>
    );
};

// --- PAGE: STAFF DETAILS (NEW PROFILE VIEW) ---
const StaffDetailsPage = ({ staff, clients, onBack, onRefresh }) => {
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState('account');
    const [loading, setLoading] = useState(false);

    // Editable State
    const [form, setForm] = useState({
        first_name: staff?.first_name || '',
        last_name: staff?.last_name || '',
        name: staff?.name || '',
        email: staff?.email || '',
        phone: staff?.phone || '',
        role: staff?.role || 'Artist',
        bio: staff?.bio || '',
        photo: staff?.photo || '',
        instagram: staff?.instagram || '',
        facebook: staff?.facebook || '',
        twitter: staff?.twitter || '',
        tiktok: staff?.tiktok || '',
        youtube: staff?.youtube || '',
        linkedin: staff?.linkedin || '',
        mobile_access: staff?.mobile_access || 1,
        pos_access: staff?.pos_access || 1,
        show_on_consent: staff?.show_on_consent || 1
    });

    const [passwords, setPasswords] = useState({ new: '', confirm: '' });
    const portfolioInputRef = useRef(null);
    const [portfolio, setPortfolio] = useState(() => {
        try { return JSON.parse(staff?.portfolio || '[]'); } catch { return []; }
    });
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    useEffect(() => {
        if (!staff) {
            const timer = setTimeout(() => { if (!staff) onBack(); }, 1000);
            return () => clearTimeout(timer);
        }
        setForm({
            ...form,
            ...staff,
            first_name: staff.first_name || staff.name?.split(' ')[0] || '',
            last_name: staff.last_name || staff.name?.split(' ').slice(1).join(' ') || ''
        });
    }, [staff, form, onBack]);

    if (!staff) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-[#5C5CFF] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 font-medium">Loading staff record...</p>
        </div>
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleToggle = (name) => {
        setForm(prev => ({ ...prev, [name]: prev[name] ? 0 : 1 }));
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 800000) { toast.error("File is too large. Max 800KB."); return; }
            const reader = new FileReader();
            reader.onloadend = () => setForm(prev => ({ ...prev, photo: reader.result }));
            reader.readAsDataURL(file);
        }
    };

    const saveChanges = async () => {
        setLoading(true);
        try {
            const res = await axios.put(`${API_URL}/staff/${staff.id}`, form);
            if (res.data.success) {
                toast.success("Changes saved successfully!");
                onRefresh();
            }
        } catch (error) {
                        toast.error("Failed to save changes.");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async () => {
        if (!passwords.new || passwords.new !== passwords.confirm) { toast.error("Passwords do not match"); return; }
        setLoading(true);
        try {
            await axios.put(`${API_URL}/staff/${staff.id}`, { ...form, password: passwords.new });
            toast.success("Password updated successfully!");
            setPasswords({ new: '', confirm: '' });
        } catch (error) {
            toast.error("Failed to update password.");
        } finally {
            setLoading(false);
        }
    };

    // Stats
    const staffClients = clients.filter(c => c.staff_name === staff.name);
    const totalSales = staffClients.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0);

    return (
        <div className="flex flex-col lg:flex-row gap-6 animate-fade-in-up h-full">
            {/* LEFT PROFILE CARD */}
            <div className="w-full lg:w-80 space-y-6">
                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden group">
                    <div className="h-24 bg-gradient-to-br from-[#5C5CFF] to-[#8E8EFF] relative">
                        <button
                            onClick={() => fileInputRef.current.click()}
                            className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 p-2 rounded-full backdrop-blur-md transition-all text-white border border-white/30"
                        >
                            <Camera size={16} />
                        </button>
                    </div>
                    <div className="px-6 pb-6 text-center">
                        <div className="relative -mt-12 mb-4 inline-block">
                            {form.photo ? (
                                <img src={form.photo} className="w-24 h-24 rounded-2xl object-cover ring-4 ring-white shadow-lg" alt="Profile" />
                            ) : (
                                <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center text-4xl font-bold text-slate-400 ring-4 ring-white shadow-lg uppercase">
                                    {getInitials(form.name)}
                                </div>
                            )}
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-4 border-white rounded-full"></div>
                        </div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight">{form.name}</h2>
                        <p className="text-[#5C5CFF] font-bold text-xs uppercase tracking-widest mt-1">{form.role}</p>

                        <div className="flex justify-center gap-3 mt-4">
                            {form.instagram && <a href={`https://instagram.com/${form.instagram}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-pink-50 text-pink-500 flex items-center justify-center hover:scale-110 transition-transform"><Instagram size={16} /></a>}
                            {form.facebook && <a href={`https://facebook.com/${form.facebook}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center hover:scale-110 transition-transform"><Facebook size={16} /></a>}
                            {form.twitter && <a href={`https://twitter.com/${form.twitter}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-sky-50 text-sky-500 flex items-center justify-center hover:scale-110 transition-transform"><Twitter size={16} /></a>}
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-slate-50/50 space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                            <Mail size={16} className="text-slate-400" />
                            <span className="text-slate-600 truncate">{form.email}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <Smartphone size={16} className="text-slate-400" />
                            <span className="text-slate-600">{form.phone || 'No phone'}</span>
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-50">
                        <button
                            disabled={loading}
                            onClick={saveChanges}
                            className="w-full bg-[#5C5CFF] hover:bg-[#4b4be6] text-white py-3 rounded-xl font-bold text-xs uppercase shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2 active:scale-95"
                        >
                            {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Save size={16} />}
                            SAVE PROFILE
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Quick Stats</h4>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Total Sales</span>
                            <span className="text-sm font-bold text-slate-800">£{totalSales.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Agreements</span>
                            <span className="text-sm font-bold text-slate-800">{staffClients.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE CONTENT */}
            <div className="flex-1 flex flex-col gap-6 overflow-hidden">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-2 overflow-x-auto custom-scrollbar no-scrollbar">
                    <div className="flex gap-2 min-w-max">
                        {[
                            { id: 'account', label: 'Account', icon: <User size={16} /> },
                            { id: 'security', label: 'Security', icon: <Lock size={16} /> },
                            { id: 'marketing', label: 'Portfolio', icon: <ImageIcon size={16} /> },
                            { id: 'settings', label: 'Permissions', icon: <Shield size={16} /> }
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === t.id ? 'bg-[#F1F1FF] text-[#5C5CFF]' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {t.icon} {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* TAB CONTENT: ACCOUNT */}
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm space-y-8 animate-fade-in-up">
                                <div>
                                    <h3 className="text-lg font-black text-slate-800 tracking-tight mb-2">Personal Information</h3>
                                    <p className="text-sm text-slate-500 mb-6">Update artist profile details and public bio.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <Input label="First Name" name="first_name" value={form.first_name} onChange={handleInputChange} />
                                        <Input label="Surname" name="last_name" value={form.last_name} onChange={handleInputChange} />
                                        <Input label="Email Address" name="email" value={form.email} onChange={handleInputChange} />
                                        <Input label="Phone Number" name="phone" value={form.phone} onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-black text-slate-800 tracking-tight mb-4">Artist Bio</h3>
                                    <textarea
                                        name="bio"
                                        placeholder="Write a brief bio about the artist..."
                                        className="w-full border border-slate-100 rounded-2xl p-5 text-sm bg-slate-50/50 min-h-[150px] outline-none focus:border-[#5C5CFF] transition-all"
                                        value={form.bio || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div>
                                    <h3 className="text-lg font-black text-slate-800 tracking-tight mb-4 text-pink-500 flex items-center gap-2">
                                        <Instagram size={20} /> Social Media Links
                                    </h3>
                                    <div className="grid grid-cols-2 gap-6">
                                        <Input label="Instagram Handle" name="instagram" value={form.instagram} onChange={handleInputChange} icon={<Instagram size={18} />} />
                                        <Input label="Facebook URL" name="facebook" value={form.facebook} onChange={handleInputChange} icon={<Facebook size={18} />} />
                                        <Input label="Twitter / X" name="twitter" value={form.twitter} onChange={handleInputChange} icon={<Twitter size={18} />} />
                                        <Input label="TikTok" name="tiktok" value={form.tiktok} onChange={handleInputChange} icon={<Smartphone size={18} />} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm animate-fade-in-up">
                                <h3 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                                    <Lock size={20} className="text-red-500" /> Security Settings
                                </h3>
                                <div className="space-y-6 max-w-md">
                                    <div className="space-y-4">
                                        <Input
                                            type="password"
                                            label="New Password"
                                            value={passwords.new}
                                            onChange={e => setPasswords({ ...passwords, new: e.target.value })}
                                        />
                                        <Input
                                            type="password"
                                            label="Confirm Password"
                                            value={passwords.confirm}
                                            onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                                        />
                                    </div>
                                    <button
                                        disabled={loading}
                                        onClick={handlePasswordChange}
                                        className="bg-[#5C5CFF] text-white px-8 py-3 rounded-xl font-bold text-xs uppercase shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                                    >
                                        Update Password
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'marketing' && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm animate-fade-in-up">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                                            <ImageIcon size={20} className="text-purple-500" /> Portfolio Gallery
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1">Upload your best work to showcase to clients</p>
                                    </div>
                                    <button
                                        onClick={() => portfolioInputRef.current?.click()}
                                        disabled={uploadingPhoto}
                                        className="bg-[#5C5CFF] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase flex items-center gap-2 hover:bg-[#4b4be6] shadow-lg shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {uploadingPhoto ? (
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Plus size={16} />
                                        )}
                                        UPLOAD PHOTO
                                    </button>
                                    <input
                                        type="file"
                                        ref={portfolioInputRef}
                                        className="hidden"
                                        accept="image/*"
                                        multiple
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files);
                                            if (files.length === 0) return;
                                            setUploadingPhoto(true);
                                            try {
                                                const newImages = [];
                                                for (const file of files) {
                                                    if (file.size > 2000000) {
                                                        toast.error(`${file.name} is too large (max 2MB). Skipping.`);
                                                        continue;
                                                    }
                                                    const compressed = await compressImage(file, 1200, 1200, 0.8);
                                                    newImages.push({
                                                        id: Date.now() + Math.random().toString(36).substring(2),
                                                        src: compressed,
                                                        caption: '',
                                                        uploaded_at: new Date().toISOString()
                                                    });
                                                }
                                                const updated = [...portfolio, ...newImages];
                                                setPortfolio(updated);
                                                // Save to backend
                                                await axios.put(`${API_URL}/staff/${staff.id}`, { portfolio: JSON.stringify(updated) });
                                            } catch (err) {
                                                                                                toast.error('Failed to upload one or more photos.');
                                            } finally {
                                                setUploadingPhoto(false);
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                </div>

                                {portfolio.length === 0 ? (
                                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 sm:p-12 text-center">
                                        <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <ImageIcon size={28} className="text-purple-400" />
                                        </div>
                                        <h4 className="text-lg font-bold text-slate-700 mb-2">No Portfolio Photos Yet</h4>
                                        <p className="text-sm text-slate-400 mb-4">Upload your best tattoo work to build your portfolio</p>
                                        <button
                                            onClick={() => portfolioInputRef.current?.click()}
                                            className="bg-purple-100 text-purple-600 px-6 py-2.5 rounded-xl font-bold text-xs uppercase hover:bg-purple-200 transition"
                                        >
                                            Upload Your First Photo
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {portfolio.map((img, idx) => (
                                            <div key={img.id || idx} className="relative group rounded-xl overflow-hidden border border-slate-100 shadow-sm aspect-square cursor-pointer" onClick={() => setLightboxIndex(idx)}>
                                                <img src={img.src} alt={img.caption || `Portfolio ${idx + 1}`} className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-between p-3">
                                                    <span className="text-white text-xs font-medium truncate">{img.caption || `Photo ${idx + 1}`}</span>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                                                            className="w-7 h-7 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-lg flex items-center justify-center text-white transition-all"
                                                            title="View fullscreen"
                                                        >
                                                            <Maximize2 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (!window.confirm('Remove this photo from your portfolio?')) return;
                                                                const updated = portfolio.filter((_, i) => i !== idx);
                                                                setPortfolio(updated);
                                                                try {
                                                                    await axios.put(`${API_URL}/staff/${staff.id}`, { portfolio: JSON.stringify(updated) });
                                                                } catch (err) {
                                                                    toast.error('Failed to remove photo.');
                                                                }
                                                            }}
                                                            className="w-7 h-7 bg-red-500/90 hover:bg-red-600 rounded-lg flex items-center justify-center text-white transition-all"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {portfolio.length > 0 && (
                                    <div className="mt-4 text-right text-xs text-slate-400">
                                        {portfolio.length} photo{portfolio.length !== 1 ? 's' : ''} in portfolio
                                    </div>
                                )}

                                {/* Fullscreen Lightbox */}
                                {lightboxIndex !== null && portfolio[lightboxIndex] && (
                                    <div
                                        className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
                                        onClick={() => setLightboxIndex(null)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') setLightboxIndex(null);
                                            if (e.key === 'ArrowRight') setLightboxIndex(prev => prev < portfolio.length - 1 ? prev + 1 : 0);
                                            if (e.key === 'ArrowLeft') setLightboxIndex(prev => prev > 0 ? prev - 1 : portfolio.length - 1);
                                        }}
                                        tabIndex={0}
                                        ref={(el) => el && el.focus()}
                                    >
                                        {/* Close Button */}
                                        <button
                                            onClick={() => setLightboxIndex(null)}
                                            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all z-10 border border-white/20"
                                        >
                                            <X size={24} />
                                        </button>

                                        {/* Counter */}
                                        <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-bold border border-white/20">
                                            {lightboxIndex + 1} / {portfolio.length}
                                        </div>

                                        {/* Previous Button */}
                                        {portfolio.length > 1 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => prev > 0 ? prev - 1 : portfolio.length - 1); }}
                                                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all border border-white/20"
                                            >
                                                <ChevronLeft size={28} />
                                            </button>
                                        )}

                                        {/* Image */}
                                        <img
                                            src={portfolio[lightboxIndex].src}
                                            alt={portfolio[lightboxIndex].caption || `Portfolio ${lightboxIndex + 1}`}
                                            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                                            onClick={(e) => e.stopPropagation()}
                                        />

                                        {/* Next Button */}
                                        {portfolio.length > 1 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => prev < portfolio.length - 1 ? prev + 1 : 0); }}
                                                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all border border-white/20"
                                            >
                                                <ChevronRight size={28} />
                                            </button>
                                        )}

                                        {/* Caption */}
                                        {portfolio[lightboxIndex].caption && (
                                            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-xl text-sm font-medium border border-white/20 max-w-lg text-center">
                                                {portfolio[lightboxIndex].caption}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm animate-fade-in-up">
                                <h3 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                                    <Shield size={20} className="text-[#5C5CFF]" /> Roles & Permissions
                                </h3>
                                <div className="space-y-2 divide-y divide-slate-50">
                                    <div className="py-4 flex justify-between items-center">
                                        <div>
                                            <div className="font-bold text-sm text-slate-700">Display on Public Consent</div>
                                            <div className="text-xs text-slate-400">Allows customers to select this artist during signing.</div>
                                        </div>
                                        <ToggleItem active={form.show_on_consent === 1} onClick={() => handleToggle('show_on_consent')} />
                                    </div>
                                    <div className="py-4 flex justify-between items-center">
                                        <div>
                                            <div className="font-bold text-sm text-slate-700">Mobile App Access</div>
                                            <div className="text-xs text-slate-400">Staff can login to the BookInk Mobile App.</div>
                                        </div>
                                        <ToggleItem active={form.mobile_access === 1} onClick={() => handleToggle('mobile_access')} />
                                    </div>
                                    <div className="py-4 flex justify-between items-center">
                                        <div>
                                            <div className="font-bold text-sm text-slate-700">POS Integration</div>
                                            <div className="text-xs text-slate-400">Allow payments and tip processing.</div>
                                        </div>
                                        <ToggleItem active={form.pos_access === 1} onClick={() => handleToggle('pos_access')} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handlePhotoUpload}
            />
        </div>
    );
};

const StaffPage = ({ staffList, onAdd, onEdit, onDelete, onViewProfile }) => (
    <div className="space-y-6 animate-fade-in-up pb-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-700">Manage Staff</h2>
                <p className="text-xs text-slate-500 mt-1">Manage artists, profiles and system access.</p>
            </div>
            <button onClick={onAdd} className="w-full sm:w-auto bg-[#5C5CFF] text-white px-6 py-2.5 rounded-md font-bold text-sm flex items-center justify-center gap-2 shadow-sm hover:bg-[#4b4be6]"><Plus size={16} /> Add Artist</button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-[#f8f9fa] text-xs uppercase font-bold text-slate-500 border-b border-slate-200">
                    <tr>
                        <th className="px-6 py-4">Name</th>
                        <th className="px-6 py-4">Phone</th>
                        <th className="px-6 py-4">Email</th>
                        <th className="px-6 py-4">Role</th>
                        <th className="px-6 py-4 text-center">Status</th>
                        <th className="px-6 py-4 text-right">Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {staffList.map(s => (
                        <tr key={s.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    {s.photo ? (
                                        <img src={s.photo} alt={s.name} className="w-8 h-8 rounded-full object-cover" />
                                    ) : (
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getColorClass(s.name)}`}>
                                            {getInitials(s.name)}
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-bold text-slate-700">{s.name}</div>
                                        <div className="text-xs text-slate-400">@{s.username || 'No username'}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">{s.phone || '-'}</td>
                            <td className="px-6 py-4">{s.email || '-'}</td>
                            <td className="px-6 py-4">{s.role || '-'}</td>
                            <td className="px-6 py-4 text-center">
                                {Number(s.is_active) === 0 ? (
                                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-bold">Inactive</span>
                                ) : (
                                    <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-bold">Active</span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <button onClick={() => onViewProfile(s)} className="bg-[#EBEBFF] text-[#5C5CFF] hover:bg-[#dadaff] px-3 py-1 rounded text-[10px] font-bold uppercase mr-2">Profile</button>
                                <button onClick={() => onEdit(s)} className="text-slate-400 hover:text-[#5C5CFF] p-1.5"><Edit size={16} /></button>
                                <button onClick={() => onDelete(s)} className="text-slate-400 hover:text-red-500 p-1.5 ml-1"><Trash2 size={16} /></button>
                            </td>
                        </tr>
                    ))}
                    {staffList.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-400">No staff members yet.</td></tr>}
                </tbody>
            </table>
        </div>
    </div>
);

// ----------------------------------------------------------------------
// PAGE: EMAIL SETTINGS (NEW)
// ----------------------------------------------------------------------
const EmailSettingsPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('consent');
    const [settings, setSettings] = useState({
        logo: '', email_company_name: '', email_title: '', email_footer: '',
        campaign_header_gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
        campaign_btn_text: 'Book Your Session \u2192', campaign_btn_color: '#6366f1',
        campaign_greeting: 'Hey', campaign_cta_url: '', campaign_accent_color: '#a855f7'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const gradientPresets = [
        { name: 'Purple Dream', value: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' },
        { name: 'Ocean Blue', value: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 50%, #4f46e5 100%)' },
        { name: 'Sunset', value: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)' },
        { name: 'Emerald', value: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)' },
        { name: 'Rose Gold', value: 'linear-gradient(135deg, #f472b6 0%, #e879f9 50%, #a78bfa 100%)' },
        { name: 'Dark Slate', value: 'linear-gradient(135deg, #334155 0%, #1e293b 50%, #0f172a 100%)' },
        { name: 'Gold Rush', value: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)' },
        { name: 'Crimson', value: 'linear-gradient(135deg, #dc2626 0%, #991b1b 50%, #7f1d1d 100%)' },
    ];

    useEffect(() => {
        axios.get(`${API_URL}/studios/email-settings`)
            .then(res => {
                setSettings({
                    logo: res.data.logo || '', email_company_name: res.data.email_company_name || user.studio_name || '',
                    email_title: res.data.email_title || 'Consent Form Copy', email_footer: res.data.email_footer || '',
                    campaign_header_gradient: res.data.campaign_header_gradient || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                    campaign_btn_text: res.data.campaign_btn_text || 'Book Your Session \u2192', campaign_btn_color: res.data.campaign_btn_color || '#6366f1',
                    campaign_greeting: res.data.campaign_greeting || 'Hey', campaign_cta_url: res.data.campaign_cta_url || '',
                    campaign_accent_color: res.data.campaign_accent_color || '#a855f7'
                });
                setLoading(false);
            }).catch(err => {  setLoading(false); });
    }, [user.studio_name]);

    const handleSave = async () => {
        setSaving(true);
        try { await axios.put(`${API_URL}/studios/email-settings`, settings); toast.success("Settings saved!"); }
        catch (err) {  toast.error("Failed to save."); }
        finally { setSaving(false); }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const compressed = await compressImage(file, 400, 400, 0.8);
        setSettings({ ...settings, logo: compressed });
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#5C5CFF] border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in-up font-sans">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-700">Email Branding & Campaigns</h2>
                    <p className="text-xs text-slate-400">Customize emails and campaign templates for your studio.</p>
                </div>
                <button onClick={handleSave} disabled={saving} className="w-full sm:w-auto bg-[#5C5CFF] text-white px-6 py-2.5 rounded-lg font-bold text-sm uppercase shadow-lg hover:bg-[#4b4be6] transition disabled:opacity-50 flex items-center justify-center gap-2">
                    {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                    {saving ? 'SAVING...' : 'SAVE ALL'}
                </button>
            </div>

            {/* TAB SWITCHER */}
            <div className="flex flex-col sm:flex-row gap-1 bg-slate-100 p-1 rounded-xl">
                <button onClick={() => setActiveTab('consent')} className={`flex-1 py-3 rounded-lg font-bold text-sm transition flex items-center justify-center gap-2 ${activeTab === 'consent' ? 'bg-white text-[#5C5CFF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    <Mail size={16} /> Consent Emails
                </button>
                <button onClick={() => setActiveTab('campaign')} className={`flex-1 py-3 rounded-lg font-bold text-sm transition flex items-center justify-center gap-2 ${activeTab === 'campaign' ? 'bg-white text-[#5C5CFF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    <Megaphone size={16} /> Campaign Builder
                </button>
            </div>

            {/* ===== CONSENT EMAIL TAB ===== */}
            {activeTab === 'consent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2"><Building2 size={18} className="text-blue-500" /> Studio Identity</h3>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email Header / Logo</label>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="w-20 h-20 bg-slate-50 border border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden">
                                        {settings.logo ? <img src={settings.logo} alt="Logo" className="w-full h-full object-contain" /> : <ImageIcon size={24} className="text-slate-300" />}
                                    </div>
                                    <div>
                                        <label className="bg-white border border-slate-300 text-slate-600 px-3 py-1.5 rounded text-xs font-bold cursor-pointer hover:bg-slate-50">
                                            CHANGE LOGO <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                                        </label>
                                        <p className="text-[10px] text-slate-400 mt-2">Transparent PNG recommended (400x400 max)</p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Company Name</label>
                                <input className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.email_company_name} onChange={e => setSettings({ ...settings, email_company_name: e.target.value })} placeholder="Your Studio Name" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2"><Type size={18} className="text-purple-500" /> Email Content</h3>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email Subject / Title</label>
                                <input className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.email_title} onChange={e => setSettings({ ...settings, email_title: e.target.value })} placeholder="Your Signed Consent Form" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Footer / Address</label>
                                <textarea className="w-full border border-slate-300 rounded-lg p-2.5 text-sm h-24 focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.email_footer} onChange={e => setSettings({ ...settings, email_footer: e.target.value })} placeholder="122 Cornwall St, Plymouth, PL1 1NF" />
                            </div>
                        </div>
                    </div>
                    {/* Consent Email Preview */}
                    <div className="bg-slate-100 rounded-xl p-6 border border-slate-200 overflow-x-auto custom-scrollbar no-scrollbar">
                        <h3 className="font-bold text-slate-500 text-xs uppercase mb-4 tracking-widest text-center">Live Email Preview</h3>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-slate-200 min-w-[320px] max-w-[400px] mx-auto sm:scale-90 origin-top">
                            <div className="bg-[#1e1b4b] p-6 text-center">
                                {settings.logo && <img src={settings.logo} alt="Logo" className="max-h-12 mx-auto mb-2" />}
                                <h1 className="text-white text-lg font-bold uppercase tracking-wide">{settings.email_company_name || 'STUDIO NAME'}</h1>
                            </div>
                            <div className="p-6 space-y-4">
                                <h2 className="text-slate-800 font-bold text-base">{settings.email_title || 'Email Title'}</h2>
                                <p className="text-slate-500 text-xs leading-relaxed">Hi [Customer Name],<br /><br />Thank you for visiting us. This email confirms that we have successfully received your signed consent form.</p>
                                <div className="bg-slate-50 p-3 rounded-lg border-l-4 border-blue-500 text-[10px]">
                                    <p className="font-bold text-slate-600">Appointment Details</p>
                                    <p className="text-slate-400 mt-1">Date: 17/02/2026<br />Artist: Staff Name</p>
                                </div>
                                <div className="text-center py-2"><div className="inline-block bg-[#4F46E5] text-white px-6 py-2 rounded text-[10px] font-bold">VIEW SIGNED FORM</div></div>
                            </div>
                            <div className="bg-slate-50 p-4 border-t border-slate-100 text-center">
                                <p className="text-[9px] text-slate-400 font-bold uppercase">&copy; 2026 {settings.email_company_name || 'STUDIO NAME'}</p>
                                <p className="text-[9px] text-slate-300 mt-1">{settings.email_footer || 'Footer Info'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== CAMPAIGN BUILDER TAB ===== */}
            {activeTab === 'campaign' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Campaign Settings */}
                    <div className="space-y-5">
                        {/* Header Gradient */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2"><Palette size={18} className="text-purple-500" /> Header Style</h3>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Header Gradient</label>
                                <div className="grid grid-cols-2 xs:grid-cols-4 gap-2">
                                    {gradientPresets.map(p => (
                                        <button key={p.name} onClick={() => setSettings({ ...settings, campaign_header_gradient: p.value })}
                                            className={`h-12 rounded-lg border-2 transition hover:scale-105 ${settings.campaign_header_gradient === p.value ? 'border-[#5C5CFF] ring-2 ring-[#5C5CFF]/30 scale-105' : 'border-slate-200'}`}
                                            style={{ background: p.value }} title={p.name} />
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-2">Choose the banner gradient for your campaign emails</p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Accent Color</label>
                                <div className="flex items-center gap-3">
                                    <input type="color" value={settings.campaign_accent_color} onChange={e => setSettings({ ...settings, campaign_accent_color: e.target.value })} className="w-10 h-10 rounded-lg border border-slate-300 cursor-pointer" />
                                    <input className="flex-1 border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none font-mono" value={settings.campaign_accent_color} onChange={e => setSettings({ ...settings, campaign_accent_color: e.target.value })} placeholder="#a855f7" />
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">Used for name highlights and message borders</p>
                            </div>
                        </div>

                        {/* Content Options */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2"><MessageSquare size={18} className="text-blue-500" /> Content Options</h3>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Greeting Style</label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {['Hey', 'Hi', 'Hello', 'Dear'].map(g => (
                                        <button key={g} onClick={() => setSettings({ ...settings, campaign_greeting: g })}
                                            className={`py-2.5 rounded-lg text-sm font-bold transition ${settings.campaign_greeting === g ? 'bg-[#5C5CFF] text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{g}</button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2"><MousePointer size={18} className="text-green-500" /> CTA Button</h3>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Button Text</label>
                                <input className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.campaign_btn_text} onChange={e => setSettings({ ...settings, campaign_btn_text: e.target.value })} placeholder="Book Your Session \u2192" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Button Color</label>
                                <div className="flex items-center gap-3">
                                    <input type="color" value={settings.campaign_btn_color} onChange={e => setSettings({ ...settings, campaign_btn_color: e.target.value })} className="w-10 h-10 rounded-lg border border-slate-300 cursor-pointer" />
                                    <input className="flex-1 border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none font-mono" value={settings.campaign_btn_color} onChange={e => setSettings({ ...settings, campaign_btn_color: e.target.value })} placeholder="#6366f1" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Button Link (URL)</label>
                                <input className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.campaign_cta_url} onChange={e => setSettings({ ...settings, campaign_cta_url: e.target.value })} placeholder="https://your-booking-site.com" />
                                <p className="text-[10px] text-slate-400 mt-1">Where users go when they click the button</p>
                            </div>
                        </div>
                    </div>

                    {/* Campaign Live Preview */}
                    <div className="bg-slate-800 rounded-xl p-4 sm:p-6 border border-slate-700 lg:sticky lg:top-6 self-start overflow-x-auto custom-scrollbar no-scrollbar">
                        <h3 className="font-bold text-slate-400 text-xs uppercase mb-4 tracking-widest text-center">Live Campaign Preview</h3>
                        <div className="rounded-2xl overflow-hidden border border-slate-600 min-w-[300px] max-w-[380px] mx-auto" style={{ background: 'linear-gradient(180deg, #1e1e3f 0%, #141428 100%)' }}>
                            {/* Header */}
                            <div className="p-5 text-center" style={{ background: settings.campaign_header_gradient }}>
                                {settings.logo ? <img src={settings.logo} alt="Logo" className="max-h-8 mx-auto mb-2" /> : <h2 className="text-white font-bold text-xs mb-1">{settings.email_company_name || 'Your Studio'}</h2>}
                                <p className="text-white/70 text-[8px] font-semibold tracking-widest uppercase mb-1">{'\u2728'} Special Announcement</p>
                                <h1 className="text-white font-extrabold text-base">Summer Flash Sale</h1>
                            </div>
                            {/* Body */}
                            <div className="p-4 text-white/90 text-[11px] leading-relaxed">
                                <p className="text-sm font-bold mb-2">{settings.campaign_greeting} <span style={{ color: settings.campaign_accent_color }}>John</span> {'\ud83d\udc4b'}</p>
                                <div className="rounded-r-lg p-3 mb-3 text-[10px] leading-relaxed" style={{ background: 'rgba(255,255,255,0.03)', borderLeft: `3px solid ${settings.campaign_accent_color}` }}>
                                    We've got an exciting deal just for you! Don't miss out on our exclusive summer flash designs...
                                </div>
                                {/* Price Badge */}
                                <div className="text-center py-2">
                                    <p className="text-slate-400 text-[8px] uppercase tracking-widest mb-1">Session Price</p>
                                    <div className="inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-extrabold px-5 py-1.5 rounded-full">{'\ud83d\udc8e'} £150</div>
                                </div>
                                <div className="h-px bg-white/5 my-3"></div>
                                {/* CTA */}
                                <div className="text-center py-2">
                                    <div className="inline-block text-white text-[9px] font-bold px-5 py-2 rounded-full uppercase tracking-wider cursor-pointer" style={{ background: `linear-gradient(135deg, ${settings.campaign_btn_color} 0%, ${settings.campaign_accent_color} 100%)` }}>
                                        {settings.campaign_btn_text}
                                    </div>
                                </div>
                            </div>
                            {/* Footer */}
                            <div className="py-3 px-4 text-center border-t border-white/5" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                {settings.logo && <img src={settings.logo} alt="Logo" className="max-h-5 mx-auto mb-1 opacity-80" />}
                                <p className="text-white/60 text-[9px] font-bold">{settings.email_company_name || 'Your Studio'}</p>
                                <p className="text-slate-500 text-[8px] mt-0.5">&copy; 2026 All rights reserved.{settings.email_footer ? <><br />{settings.email_footer}</> : null}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};



// ----------------------------------------------------------------------
const MarketingPage = ({ campaigns, onStart, user, staffList }) => {
    const [view, setView] = useState('list'); // 'list' or 'detail'
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleViewCampaign = async (campaign) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/campaigns/${campaign.id}`);
            setSelectedCampaign(res.data);
            setView('detail');
        } catch (err) {
                        // Fallback to local data
            setSelectedCampaign(campaign);
            setView('detail');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setView('list');
        setSelectedCampaign(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-4 border-[#5C5CFF] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // Campaign Detail View
    if (view === 'detail' && selectedCampaign) {
        return (
            <div className="space-y-6 animate-fade-in-up">
                {/* Header with Back Button */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 text-slate-500 hover:text-[#5C5CFF] transition px-3 py-2 rounded-lg hover:bg-slate-100 w-fit"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-bold text-sm">Back to Campaigns</span>
                    </button>
                </div>

                {/* Campaign Details Card */}
                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-br from-[#5C5CFF] to-[#8E8EFF] p-6 text-white">
                        <h2 className="text-2xl font-bold">{selectedCampaign.title}</h2>
                        <div className="flex items-center gap-4 mt-3 text-white/80 text-sm">
                            <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {new Date(selectedCampaign.sent_at).toLocaleDateString('en-GB', {
                                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${selectedCampaign.delivery_status === 'sent' ? 'bg-green-500/20 text-green-100' :
                                selectedCampaign.delivery_status === 'failed' ? 'bg-red-500/20 text-red-100' :
                                    'bg-yellow-500/20 text-yellow-100'
                                }`}>
                                {selectedCampaign.delivery_status || 'Sent'}
                            </span>
                        </div>
                    </div>

                    <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left: Image & Content */}
                        <div className="space-y-6">
                            {selectedCampaign.image && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Campaign Image</h4>
                                    <img
                                        src={selectedCampaign.image}
                                        alt="Campaign"
                                        className="w-full max-h-80 object-cover rounded-xl border border-slate-200 shadow-sm"
                                    />
                                </div>
                            )}

                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Message Content</h4>
                                <div className="bg-slate-50 rounded-xl p-5 text-slate-700 whitespace-pre-wrap leading-relaxed">
                                    {selectedCampaign.message}
                                </div>
                            </div>

                            {selectedCampaign.price && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Pricing</h4>
                                    <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold text-lg shadow-lg shadow-green-200">
                                        💰 {selectedCampaign.price}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: Stats & Recipients */}
                        <div className="space-y-6">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-purple-50 rounded-xl p-5 text-center">
                                    <div className="text-3xl font-black text-purple-600">{selectedCampaign.audience_count || 0}</div>
                                    <div className="text-xs text-purple-400 font-bold uppercase tracking-widest mt-1">Target Audience</div>
                                </div>
                                <div className="bg-green-50 rounded-xl p-5 text-center">
                                    <div className="text-3xl font-black text-green-600">{selectedCampaign.sent_count || selectedCampaign.audience_count || 0}</div>
                                    <div className="text-xs text-green-400 font-bold uppercase tracking-widest mt-1">Emails Sent</div>
                                </div>
                            </div>

                            <div className="bg-slate-50 rounded-xl p-5">
                                <div className="flex items-center gap-2 text-slate-500 text-sm">
                                    <Users size={16} />
                                    <span className="font-bold">Audience Type:</span>
                                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${selectedCampaign.audience_type === 'artist' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                        }`}>
                                        {selectedCampaign.audience_type === 'artist' ? 'Artist Folder' : 'Studio Folder'}
                                    </span>
                                </div>
                                {selectedCampaign.artist_name && (
                                    <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
                                        <User size={16} />
                                        <span className="font-bold">Artist:</span>
                                        <span>{selectedCampaign.artist_name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Recipients List */}
                            {selectedCampaign.recipients && selectedCampaign.recipients.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Recipients ({selectedCampaign.recipients.length})</h4>
                                    <div className="bg-white border border-slate-200 rounded-xl max-h-64 overflow-y-auto">
                                        {selectedCampaign.recipients.slice(0, 50).map((r, i) => (
                                            <div key={i} className="px-4 py-3 border-b border-slate-50 last:border-0 flex items-center gap-3 hover:bg-slate-50">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5C5CFF] to-[#8E8EFF] flex items-center justify-center text-white text-xs font-bold">
                                                    {(r.name || r.email || '?')[0].toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-slate-700 truncate">{r.name || 'Unknown'}</div>
                                                    <div className="text-xs text-slate-400 truncate">{r.email}</div>
                                                </div>
                                            </div>
                                        ))}
                                        {selectedCampaign.recipients.length > 50 && (
                                            <div className="px-4 py-3 text-center text-xs text-slate-400">
                                                +{selectedCampaign.recipients.length - 50} more recipients
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Campaign List View
    return (
        <div className="space-y-6 animate-fade-in-up">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-black text-slate-700 uppercase tracking-tight">Marketing Hub</h2>
                    <p className="text-sm text-slate-500 mt-1">Create and track promotional campaigns</p>
                </div>
                <button
                    onClick={onStart}
                    className="w-full sm:w-auto bg-gradient-to-r from-[#5C5CFF] to-[#8E8EFF] text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                    <Plus size={18} />
                    NEW CAMPAIGN
                </button>
            </div>

            {/* Campaign List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto custom-scrollbar no-scrollbar">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 font-bold text-slate-500 border-b border-slate-200">
                        <tr>
                            <th className="p-4">Campaign</th>
                            <th className="p-4">Audience</th>
                            <th className="p-4">Sent To</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Date</th>
                            <th className="p-4 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {campaigns.map(c => (
                            <tr key={c.id} className="hover:bg-slate-50 transition cursor-pointer" onClick={() => handleViewCampaign(c)}>
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        {c.image ? (
                                            <img src={c.image} alt="Campaign" className="w-10 h-10 rounded-lg object-cover border border-slate-200" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                                                <Megaphone size={18} />
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-bold text-slate-700">{c.title}</div>
                                            {c.price && <div className="text-xs text-green-600 font-medium">{c.price}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    {c.audience_type === 'artist' ?
                                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide">Artist</span> :
                                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide">Studio</span>
                                    }
                                </td>
                                <td className="p-4 text-slate-600 font-medium">{c.sent_count || c.audience_count || 0} Clients</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${c.delivery_status === 'sent' ? 'bg-green-100 text-green-700' :
                                        c.delivery_status === 'failed' ? 'bg-red-100 text-red-700' :
                                            c.delivery_status === 'sending' ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-slate-100 text-slate-500'
                                        }`}>
                                        {c.delivery_status || 'Sent'}
                                    </span>
                                </td>
                                <td className="p-4 text-slate-500">{new Date(c.sent_at).toLocaleDateString()}</td>
                                <td className="p-4 text-right">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleViewCampaign(c); }}
                                        className="bg-[#EBEBFF] text-[#5C5CFF] hover:bg-[#dadaff] px-3 py-1.5 rounded text-xs font-bold uppercase"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {campaigns.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-6 sm:p-12 text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                                        <Megaphone size={28} className="text-purple-500" />
                                    </div>
                                    <div className="text-slate-500 font-medium">No campaigns yet</div>
                                    <p className="text-sm text-slate-400 mt-1">Create your first campaign to engage with your clients</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// --- HELPER COMPONENTS ---
const ToggleItem = ({ label, active, onClick }) => (
    <div className="flex justify-between items-center cursor-pointer" onClick={onClick}>
        <span className="text-xs text-slate-500 font-medium">{label}</span>
        <div className={`w-10 h-5 rounded-full relative transition-colors duration-200 ease-in-out ${active ? 'bg-[#5C5CFF]' : 'bg-slate-200'}`}>
            <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all duration-200 ease-in-out ${active ? 'left-6' : 'left-1'}`}></div>
        </div>
    </div>
);

const SidebarItem = ({ icon, label, active, onClick, dark }) => (
    <div onClick={onClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition text-sm font-medium touch-manipulation ${active ? 'bg-[#5C5CFF] text-white shadow-md' : dark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>{icon}<span>{label}</span>{active && <ChevronRight size={14} className="ml-auto opacity-50" />}</div>
);

const Input = ({ label, value, type = "text", name, onChange, icon }) => (
    <div className="relative group">
        <div className="absolute top-3 left-3 text-slate-400 pointer-events-none">{icon}</div>
        <input
            type={type}
            name={name}
            className={`peer w-full border border-slate-200 rounded-md py-2.5 ${icon ? 'pl-10' : 'pl-3'} pr-3 text-slate-700 placeholder-transparent focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm`}
            placeholder={label}
            value={value || ''}
            onChange={onChange}
        />
        <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-400 peer-placeholder-shown:top-2.5 peer-placeholder-shown:left-3 peer-focus:-top-2 peer-focus:left-2 peer-focus:text-xs peer-focus:text-[#5C5CFF]">{label}</label>
    </div>
);


// --- SEND CONSENT FORM MODAL (Existing vs New) ---
const SendConsentFormModal = ({ user, clients, form, selectedStudioId, onClose, onRefresh }) => {
    const [tab, setTab] = useState('existing'); // 'existing' or 'new'
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // New Client Form
    const [newClient, setNewClient] = useState({
        first_name: '', last_name: '', email: ''
    });

    const filteredClients = clients.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 5);

    const handleSend = async (clientData) => {
        if (!clientData.email || !clientData.first_name) { toast.error("Missing required info"); return; }

        setLoading(true);
        try {
            const targetStudioId = user?.role === 'superadmin'
                ? (selectedStudioId || form?.studio_id)
                : (user?.studio_id || user?.id);

            if (!targetStudioId) {
                toast.error("Please select a studio before sending the form.");
                setLoading(false);
                return;
            }

            const res = await axios.post(`${API_URL}/forms/send`, {
                email: clientData.email,
                first_name: clientData.first_name,
                last_name: clientData.last_name,
                studio_id: targetStudioId,
                form_id: form?.id
            });

            if (res.data.success) {
                toast.success("Consent form request sent successfully!");
                onRefresh();
                onClose();
            }
        } catch (e) {
                        // Handle duplicate email (409 Conflict)
            if (e.response?.status === 409 && e.response?.data?.duplicate) {
                const data = e.response.data;
                const shouldResend = window.confirm(
                    `${data.message}\n\nClick OK to resend the consent form to this email.`
                );
                if (shouldResend) {
                    try {
                        const resendRes = await axios.post(`${API_URL}/clients/${data.existing_client_id}/email`);
                        toast.success(resendRes.data.message || "Form resent successfully!");
                        onRefresh();
                        onClose();
                    } catch (resendErr) {
                        toast.error("Failed to resend form. Please try again.");
                    }
                }
            } else {
                toast.error(e.response?.data?.message || "Failed to send form request.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[1001] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg animate-fade-in-up">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800">Send Consent Form</h2>
                        <p className="text-sm text-slate-500">Requesting signature for: <span className="text-[#666CFF] font-bold">{form?.title || 'Form'}</span></p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={24} className="text-slate-400" /></button>
                </div>

                {/* TABS */}
                <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                    <button
                        onClick={() => setTab('existing')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${tab === 'existing' ? 'bg-white text-[#666CFF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Existing Customer
                    </button>
                    <button
                        onClick={() => setTab('new')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${tab === 'new' ? 'bg-white text-[#666CFF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        New Customer
                    </button>
                </div>

                {tab === 'existing' ? (
                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#666CFF] transition-all"
                            />
                        </div>

                        <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                            {filteredClients.map(c => (
                                <div
                                    key={c.id}
                                    onClick={() => setSelectedClient(c)}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between ${selectedClient?.id === c.id ? 'border-[#666CFF] bg-indigo-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                                >
                                    <div>
                                        <p className="font-bold text-slate-800">{c.first_name} {c.last_name}</p>
                                        <p className="text-xs text-slate-500">{c.email}</p>
                                    </div>
                                    {selectedClient?.id === c.id && <Check size={18} className="text-[#666CFF]" />}
                                </div>
                            ))}
                            {search && filteredClients.length === 0 && (
                                <p className="text-center text-sm text-slate-400 py-4">No customers found.</p>
                            )}
                        </div>

                        <button
                            disabled={!selectedClient || loading}
                            onClick={() => handleSend(selectedClient)}
                            className="w-full bg-[#666CFF] text-white py-4 rounded-xl font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#555BDD] shadow-lg shadow-indigo-100 transition-all disabled:opacity-50 disabled:grayscale"
                        >
                            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Send size={18} /> Send to {selectedClient?.first_name || 'Customer'}</>}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-1">First Name</label>
                                <input
                                    type="text"
                                    value={newClient.first_name}
                                    onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                                    placeholder="First Name"
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#666CFF]"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Last Name</label>
                                <input
                                    type="text"
                                    value={newClient.last_name}
                                    onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                                    placeholder="Doe"
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#666CFF]"
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Email Address</label>
                            <input
                                type="email"
                                value={newClient.email}
                                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                                placeholder="john.doe@example.com"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#666CFF]"
                            />
                        </div>

                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mt-4">
                            <p className="text-xs text-blue-700 leading-relaxed font-medium">
                                ℹ️ We'll send an email with a secure link. Once signed, the customer will be automatically added to your system.
                            </p>
                        </div>

                        <button
                            disabled={!newClient.email || !newClient.first_name || loading}
                            onClick={() => handleSend(newClient)}
                            className="w-full bg-[#666CFF] text-white py-3 sm:py-4 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#555BDD] shadow-lg shadow-indigo-100 transition-all disabled:opacity-50"
                        >
                            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Send size={18} /> Send Form Now</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};


// --- MODALS ---

const ClientModal = ({ user, onClose, onRefresh, client, staffList }) => {
    const [form, setForm] = useState({
        first_name: client?.first_name || '', middle_name: client?.middle_name || '', last_name: client?.last_name || '',
        address: client?.address || '', postcode: client?.postcode || '', phone: client?.phone || '', email: client?.email || '',
        appt_date: client?.appt_date ? formatDateForInput(client.appt_date) : '', staff_name: client?.staff_name || ''
    });
    const handleSave = async () => {
        if (!form.first_name || !form.last_name || !form.email) { toast.error("Fill required fields"); return; }
        const payload = { ...form, studio_id: user.id };
        try {
            const res = client
                ? await axios.put(`${API_URL}/clients/${client.id}`, payload)
                : await axios.post(`${API_URL}/clients`, payload);

            if (res.data.success) {
                onRefresh();
                onClose();
            } else {
                toast.error(res.data.message || 'Error saving client');
            }
        } catch (e) {
                        toast.error(e.response?.data?.message || 'Error saving client');
        }
    };
    const handleInputChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-slate-700">{client ? 'Edit Customer' : 'Add New Customer'}</h2>
                    <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-red-500" /></button>
                </div>
                <p className="text-sm text-slate-500 mb-6">Enter details below to update your customer record.</p>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <input name="first_name" value={form.first_name} onChange={handleInputChange} placeholder="First Name" className="border border-slate-300 rounded p-2 text-sm" />
                        <input name="middle_name" value={form.middle_name} onChange={handleInputChange} placeholder="Middle Name" className="border border-slate-300 rounded p-2 text-sm" />
                        <input name="last_name" value={form.last_name} onChange={handleInputChange} placeholder="Surname" className="border border-slate-300 rounded p-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <input name="address" value={form.address} onChange={handleInputChange} placeholder="Address" className="sm:col-span-2 border border-slate-300 rounded p-2 text-sm" />
                        <input name="postcode" value={form.postcode} onChange={handleInputChange} placeholder="Postcode" className="border border-slate-300 rounded p-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input name="phone" value={form.phone} onChange={handleInputChange} placeholder="Phone Number" className="border border-slate-300 rounded p-2 text-sm" />
                        <input name="email" value={form.email} onChange={handleInputChange} placeholder="Email Address" className="border border-slate-300 rounded p-2 text-sm" />
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
                    <button onClick={onClose} className="order-2 sm:order-1 bg-gray-500 text-white px-6 py-2.5 rounded text-sm font-bold uppercase">CLOSE</button>
                    <button onClick={handleSave} className="order-1 sm:order-2 bg-[#5C5CFF] text-white px-6 py-2.5 rounded text-sm font-bold uppercase flex items-center justify-center gap-2 hover:bg-[#4b4be6]">
                        <Check size={16} /> SAVE NOW
                    </button>
                </div>
            </div>
        </div>
    );
};



const StaffModal = ({ user, onClose, onRefresh, staff }) => {
    const [form, setForm] = useState({
        first_name: staff?.first_name || (staff?.name ? staff.name.split(' ')[0] : ''),
        last_name: staff?.last_name || (staff?.name ? staff.name.split(' ').slice(1).join(' ') : ''),
        email: staff?.email || '',
        phone: staff?.phone || '',
        role: staff?.role || 'Artist',
        mobile_access: staff?.mobile_access !== undefined ? staff.mobile_access : 1,
        pos_access: staff?.pos_access !== undefined ? staff.pos_access : 1,
        show_on_consent: staff?.show_on_consent !== undefined ? staff.show_on_consent : 1,
        is_active: staff?.is_active !== undefined ? staff.is_active : 1
    });

    const handleInputChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const handleSelectChange = (e) => setForm({ ...form, [e.target.name]: parseInt(e.target.value) });

    const handleSave = async () => {
        if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()) {
            toast.error("First Name, Last Name and Email are required."); return;
        }
        try {
            const payload = { ...form, studio_id: user.studio_id || user.id };
            const res = staff
                ? await axios.put(`${API_URL}/staff/${staff.id}`, payload)
                : await axios.post(`${API_URL}/staff`, payload);

            if (res.data.success) {
                onRefresh();
                onClose();
            } else {
                toast.error(res.data.message || 'Error saving staff');
            }
        } catch (error) {
                        toast.error(error.response?.data?.message || 'Error saving staff. Check your connection.');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 md:p-10 w-full max-w-2xl animate-fade-in-up font-sans relative overflow-y-auto max-h-[90vh]">
                <button onClick={onClose} className="absolute right-4 top-4 sm:right-6 sm:top-6 text-slate-400 hover:text-slate-600"><X size={20} /></button>
                <div className="text-center w-full mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-700">Staff Information</h2>
                    <p className="text-slate-400 text-xs sm:text-sm mt-1">Manage artist profile and system access.</p>
                </div>

                <div className="space-y-6">
                    {/* Names */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <Input label="First Name" name="first_name" value={form.first_name} onChange={handleInputChange} icon={<User size={16} />} />
                        <Input label="Last Name" name="last_name" value={form.last_name} onChange={handleInputChange} icon={<User size={16} />} />
                    </div>

                    {/* Email */}
                    <Input label="Email Address" name="email" value={form.email} onChange={handleInputChange} icon={<Mail size={16} />} />

                    {/* Phone & Role */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <Input label="Phone Number" name="phone" value={form.phone} onChange={handleInputChange} icon={<Smartphone size={16} />} />
                        <div className="relative group">
                            <select name="role" value={form.role} onChange={handleInputChange} className="peer w-full border border-slate-200 rounded-md py-2.5 px-3 text-slate-700 focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm bg-white appearance-none">
                                <option value="Artist">Artist</option>
                                <option value="Manager">Manager</option>
                                <option value="Receptionist">Receptionist</option>
                            </select>
                            <div className="absolute right-3 top-3 text-slate-400 pointer-events-none"><ChevronDown size={14} /></div>
                            <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500">Account Type</label>
                        </div>
                    </div>

                    {/* Permissions */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div className="relative group">
                            <select name="mobile_access" value={form.mobile_access} onChange={handleSelectChange} className="peer w-full border border-slate-200 rounded-md py-2.5 px-3 text-slate-700 focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm bg-white appearance-none">
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                            <div className="absolute right-3 top-3 text-slate-400 pointer-events-none"><ChevronDown size={14} /></div>
                            <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500">Mobile Access</label>
                        </div>
                        <div className="relative group">
                            <select name="pos_access" value={form.pos_access} onChange={handleSelectChange} className="peer w-full border border-slate-200 rounded-md py-2.5 px-3 text-slate-700 focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm bg-white appearance-none">
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                            <div className="absolute right-3 top-3 text-slate-400 pointer-events-none"><ChevronDown size={14} /></div>
                            <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500">Access POS</label>
                        </div>
                        <div className="relative group">
                            <select name="show_on_consent" value={form.show_on_consent} onChange={handleSelectChange} className="peer w-full border border-slate-200 rounded-md py-2.5 px-3 text-slate-700 focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm bg-white appearance-none">
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                            <div className="absolute right-3 top-3 text-slate-400 pointer-events-none"><ChevronDown size={14} /></div>
                            <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500">Show On Consent</label>
                        </div>
                    </div>

                    {/* Account Status */}
                    <div className="relative group">
                        <select name="is_active" value={form.is_active} onChange={handleSelectChange} className="peer w-full border border-slate-200 rounded-md py-2.5 px-3 text-slate-700 focus:outline-none focus:border-[#5C5CFF] transition font-medium text-sm bg-white appearance-none">
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </select>
                        <div className="absolute right-3 top-3 text-slate-400 pointer-events-none"><ChevronDown size={14} /></div>
                        <label className="absolute left-2 -top-2 bg-white px-1 text-xs font-bold text-slate-500">Account Status</label>
                    </div>

                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-10">
                    <button onClick={handleSave} className="bg-[#5C5CFF] text-white px-8 py-3 rounded-md text-sm font-bold uppercase shadow-lg shadow-indigo-200 hover:bg-[#4b4be6] w-full sm:min-w-[170px]">
                        SAVE DETAILS
                    </button>
                    <button onClick={onClose} className="bg-white border border-slate-200 text-slate-500 px-8 py-3 rounded-md text-sm font-bold uppercase hover:bg-slate-50 w-full sm:min-w-[170px]">
                        CANCEL
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- PAGE: STUDIO DETAILS (ADMIN VIEW) ---
// --- PAGE: STUDIO DETAILS (ADMIN VIEW) ---
// --- MODAL: EDIT STUDIO ---
const EditStudioModal = ({ studio, user, setUser, onClose, onRefresh }) => {
    const [form, setForm] = useState({ ...studio });
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!form.studio_name?.trim() || !form.studio_email?.trim()) {
            toast.error("Studio Name and Email are required."); return;
        }
        // Password validation
        if (form.new_password || form.confirm_password) {
            if (form.new_password !== form.confirm_password) {
                toast.error("Passwords do not match."); return;
            }
            if (form.new_password.length < 6) {
                toast.error("Password must be at least 6 characters."); return;
            }
        }
        setLoading(true);
        try {
            // Build payload — include password only if set
            const payload = { ...form };
            if (form.new_password && form.new_password === form.confirm_password) {
                payload.password = form.new_password;
            }
            // Clean up non-DB fields
            delete payload.new_password;
            delete payload.confirm_password;

            await axios.put(`${API_URL}/studios/${studio.id}`, payload);
            toast.success('Settings saved successfully!' + (form.new_password ? ' Password has been updated.' : ''));

            // If the edited studio is the current user, update state and localstorage
            if (user && user.id === studio.id) {
                const updatedUser = { ...user, ...form };
                delete updatedUser.new_password;
                delete updatedUser.confirm_password;
                setUser(updatedUser);
                localStorage.setItem('bookink_user', JSON.stringify(updatedUser));
            }

            onRefresh();
            onClose();
        } catch (error) {
                        toast.error(error.response?.data?.message || 'Failed to update studio.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressed = await compressImage(file);
                setForm({ ...form, logo: compressed });
            } catch (err) {
                            }
        }
    };

    if (!studio) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl animate-fade-in-up overflow-y-auto max-h-[95vh] custom-scrollbar">
                <div className="p-5 sm:p-6 border-b border-slate-100 flex justify-between items-center bg-[#f8f9fa] sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-slate-700">Studio Settings</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-red-500 p-2 transition"><X size={20} /></button>
                </div>

                <div className="p-8 space-y-8">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                        <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-200 relative overflow-hidden group cursor-pointer shrink-0" onClick={() => fileInputRef.current?.click()}>
                            {form.logo ? (
                                <img src={form.logo} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <div className="text-3xl font-bold text-indigo-500 uppercase">{(form.studio_name || 'S')[0]}</div>
                            )}
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-white text-[10px] font-bold uppercase backdrop-blur-sm">Upload Logo</div>
                        </div>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleLogoUpload} />

                        <div className="flex-1 text-center sm:text-left">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">{form.studio_name}</h3>
                            <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mt-1">Studio ID: #{form.id}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1 tracking-widest">Studio Name</label>
                            <input className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition" value={form.studio_name} onChange={e => setForm({ ...form, studio_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1 tracking-widest">Owner Name</label>
                            <input className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition" value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1 tracking-widest">Contact Email</label>
                            <input className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition" value={form.studio_email} onChange={e => setForm({ ...form, studio_email: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1 tracking-widest">Contact Phone</label>
                            <input className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition" value={form.studio_phone} onChange={e => setForm({ ...form, studio_phone: e.target.value })} />
                        </div>
                    </div>
                </div>

                <div className="p-8 space-y-4 border-t border-slate-100">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Lock size={16} className="text-red-500" /> Reset Studio Password
                    </h3>
                    <p className="text-xs text-slate-400">Set a new login password for this studio account.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 ml-1 tracking-widest">New Password</label>
                            <input
                                type="password"
                                className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition bg-slate-50"
                                placeholder="Enter new password"
                                value={form.new_password || ''}
                                onChange={e => setForm({ ...form, new_password: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 ml-1 tracking-widest">Confirm Password</label>
                            <input
                                type="password"
                                className="w-full border border-slate-200 rounded-lg p-3 text-sm font-medium focus:border-[#5C5CFF] outline-none transition bg-slate-50"
                                placeholder="Confirm password"
                                value={form.confirm_password || ''}
                                onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                            />
                        </div>
                    </div>
                    {form.new_password && form.confirm_password && form.new_password !== form.confirm_password && (
                        <p className="text-xs text-red-500 font-medium">⚠️ Passwords do not match</p>
                    )}
                </div>

                <div className="p-6 bg-[#f8f9fa] border-t border-slate-100 flex flex-col sm:flex-row justify-end gap-3 sticky bottom-0">
                    <button onClick={onClose} className="order-2 sm:order-1 px-6 py-2.5 rounded-md font-bold text-xs uppercase text-slate-500 hover:bg-slate-200 transition">Cancel</button>
                    <button onClick={handleSave} disabled={loading} className="order-1 sm:order-2 bg-[#5C5CFF] text-white px-8 py-2.5 rounded-md font-bold text-xs uppercase shadow-lg shadow-blue-200 hover:bg-[#4b4be6] transition disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MODAL: ADD STUDIO ---
const AddStudioModal = ({ onClose, onRefresh }) => {
    const [form, setForm] = useState({
        studio_name: '',
        owner_name: '',
        studio_email: '',
        studio_phone: '',
        password: 'password123'
    });

    const handleSave = async () => {
        if (!form.studio_name || !form.studio_email) {
            toast.error('Please fill in Studio Name and Email'); return;
        }
        try {
            await axios.post(`${API_URL}/studios/register`, form);
            onRefresh();
            onClose();
            toast.success('Studio added successfully!');
        } catch (error) {
            toast.error('Failed to add studio');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 w-full max-w-lg animate-fade-in-up max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-slate-700">Add New Studio</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Studio Name *</label>
                        <input
                            className="w-full border border-slate-200 rounded p-3 text-sm focus:border-[#5C5CFF] outline-none"
                            placeholder="Ink Masters Studio"
                            value={form.studio_name}
                            onChange={e => setForm({ ...form, studio_name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Owner Name</label>
                        <input
                            className="w-full border border-slate-200 rounded p-3 text-sm focus:border-[#5C5CFF] outline-none"
                            placeholder="John Doe"
                            value={form.owner_name}
                            onChange={e => setForm({ ...form, owner_name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Studio Email *</label>
                        <input
                            type="email"
                            className="w-full border border-slate-200 rounded p-3 text-sm focus:border-[#5C5CFF] outline-none"
                            placeholder="studio@example.com"
                            value={form.studio_email}
                            onChange={e => setForm({ ...form, studio_email: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Studio Phone</label>
                        <input
                            type="tel"
                            className="w-full border border-slate-200 rounded p-3 text-sm focus:border-[#5C5CFF] outline-none"
                            placeholder="+44 123 456 7890"
                            value={form.studio_phone}
                            onChange={e => setForm({ ...form, studio_phone: e.target.value })}
                        />
                    </div>

                    <div className="bg-blue-50 p-3 rounded text-xs text-blue-700">
                        <strong>Note:</strong> Default password will be <code className="bg-blue-100 px-1 rounded">password123</code>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button onClick={handleSave} className="flex-1 bg-[#5C5CFF] text-white py-3 rounded font-bold text-sm hover:bg-[#4b4be6]">ADD STUDIO</button>
                    <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-500 py-3 rounded font-bold text-sm hover:bg-slate-50">CANCEL</button>
                </div>
            </div>
        </div>
    );
};

const CampaignModal = ({ user, onClose, onRefresh, staffList }) => {
    const [form, setForm] = useState({ title: '', message: '', audience_type: 'studio', image: null, price: '', artist_name: '', custom_emails: '' });
    const [loading, setLoading] = useState(false);
    const [audienceCount, setAudienceCount] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showOthersPopup, setShowOthersPopup] = useState(false);
    const [othersEmails, setOthersEmails] = useState('');
    const [savedLeads, setSavedLeads] = useState([]);
    const fileInputRef = useRef(null);

    // Fetch audience count when audience type or artist changes
    useEffect(() => {
        const fetchAudienceCount = async () => {
            try {
                const params = new URLSearchParams({
                    studio_id: user.id,
                    audience_type: form.audience_type
                });
                if (form.audience_type === 'artist' && form.artist_name) {
                    params.append('artist_name', form.artist_name);
                }
                const res = await axios.get(`${API_URL}/campaigns/audience-count?${params}`);
                setAudienceCount(res.data.count);
            } catch (err) {
                                setAudienceCount(null);
            }
        };

        if (form.audience_type === 'studio' || (form.audience_type === 'artist' && form.artist_name)) {
            fetchAudienceCount();
        } else {
            setAudienceCount(0);
        }
    }, [form.audience_type, form.artist_name, user.id]);

    const handleLaunchClick = () => {
        if (!form.title?.trim() || !form.message?.trim()) {
            toast.error("Campaign Title and Message are required."); return;
        }
        if (form.audience_type === 'artist' && !form.artist_name) {
            toast.error("Please select an artist for Artist campaigns."); return;
        }
        // Count custom emails
        const customEmailCount = form.custom_emails?.trim().split(/[,\n]+/).filter(e => e.trim() && e.includes('@')).length || 0;
        const totalRecipients = (audienceCount || 0) + customEmailCount;
        if (totalRecipients === 0) {
            toast.error("No recipients found. Add clients or custom emails."); return;
        }
        setShowConfirmation(true);
    };

    const handleConfirmSend = async () => {
        setShowConfirmation(false);
        setLoading(true);
        try {
            const payload = { ...form, studio_id: user.id };
            const res = await axios.post(`${API_URL}/campaigns`, payload);
            if (res.data.success) {
                toast.success(`✅ Campaign sent successfully!\n\n📧 Sent to ${res.data.sentCount} of ${res.data.audienceCount} clients.`);
                onRefresh();
                onClose();
            } else {
                toast.error(res.data.message || "Failed to start campaign");
            }
        } catch (error) {
                        toast.error(error.response?.data?.message || "Failed to start campaign");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressed = await compressImage(file, 800, 600);
                setForm({ ...form, image: compressed });
            } catch (err) {
                toast.error("Image upload failed");
            }
        }
    };

    // Get unique artist names from staffList
    const artistNames = staffList ? [...new Set(staffList.filter(s => s.name).map(s => s.name))] : [];

    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl animate-fade-in-up overflow-hidden max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-[#5C5CFF] to-[#8E8EFF]">
                        <div>
                            <h2 className="text-xl font-bold text-white">Create New Campaign</h2>
                            <p className="text-white/70 text-sm mt-0.5">Send promotional emails to your clients</p>
                        </div>
                        <button onClick={onClose} className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-lg transition">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-6 space-y-6 overflow-y-auto flex-1">
                        {/* Audience Selector with Live Count */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Target Audience</label>
                                {audienceCount !== null && (
                                    <span className="text-xs font-bold text-[#5C5CFF] bg-[#EBEBFF] px-2 py-1 rounded-full">
                                        📧 {audienceCount} client{audienceCount !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <button
                                    onClick={() => setForm({ ...form, audience_type: 'studio', artist_name: '' })}
                                    className={`p-4 rounded-xl border-2 text-sm font-bold transition flex flex-col items-center gap-2 touch-manipulation ${form.audience_type === 'studio' ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-lg shadow-purple-100' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300'}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                        <Users size={20} className="text-purple-600" />
                                    </div>
                                    <span className="text-xs">Studio</span>
                                    <span className="text-[9px] opacity-60 font-normal">All Clients</span>
                                </button>
                                <button
                                    onClick={() => setForm({ ...form, audience_type: 'artist' })}
                                    className={`p-4 rounded-xl border-2 text-sm font-bold transition flex flex-col items-center gap-2 touch-manipulation ${form.audience_type === 'artist' ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-lg shadow-blue-100' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300'}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                        <User size={20} className="text-blue-600" />
                                    </div>
                                    <span className="text-xs">Artist</span>
                                    <span className="text-[9px] opacity-60 font-normal">My Clients</span>
                                </button>
                                <button
                                    onClick={() => { setForm({ ...form, audience_type: 'others' }); setShowOthersPopup(true); }}
                                    className={`p-4 rounded-xl border-2 text-sm font-bold transition flex flex-col items-center gap-2 touch-manipulation ${form.audience_type === 'others' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-lg shadow-emerald-100' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300'}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <Plus size={20} className="text-emerald-600" />
                                    </div>
                                    <span className="text-xs">Others</span>
                                    <span className="text-[9px] opacity-60 font-normal">New Leads</span>
                                    {savedLeads.length > 0 && (
                                        <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full">{savedLeads.length} added</span>
                                    )}
                                </button>
                            </div>

                            {/* Artist Selector (shown when Artist Folder selected) */}
                            {form.audience_type === 'artist' && artistNames.length > 0 && (
                                <div className="mt-4 animate-fade-in-up">
                                    <label className="block text-xs font-bold text-slate-500 mb-1.5">Select Artist</label>
                                    <select
                                        className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm focus:border-blue-500 outline-none touch-manipulation"
                                        value={form.artist_name}
                                        onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                                    >
                                        <option value="">Choose an artist...</option>
                                        {artistNames.map(name => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Campaign Content */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">Campaign Title *</label>
                                <input
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm focus:border-[#5C5CFF] outline-none touch-manipulation"
                                    placeholder="e.g. Summer Flash Sale"
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">Message Content *</label>
                                <textarea
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm h-28 focus:border-[#5C5CFF] outline-none resize-none touch-manipulation"
                                    placeholder="Enter your promotional message here..."
                                    value={form.message}
                                    onChange={e => setForm({ ...form, message: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">Price / Session Rate</label>
                                <input
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm focus:border-[#5C5CFF] outline-none touch-manipulation"
                                    placeholder="e.g. £150 or From £80"
                                    value={form.price}
                                    onChange={e => setForm({ ...form, price: e.target.value })}
                                />
                                <p className="text-xs text-slate-400 mt-1">Optional: Show pricing in your campaign email</p>
                            </div>

                            {/* Custom Email Recipients */}
                            <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">✉️ Additional Recipients (Optional)</label>
                                <textarea
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm h-20 focus:border-[#5C5CFF] outline-none resize-none touch-manipulation font-mono"
                                    placeholder="Enter emails separated by commas or new lines:
john@example.com, jane@example.com"
                                    value={form.custom_emails}
                                    onChange={e => setForm({ ...form, custom_emails: e.target.value })}
                                />
                                <p className="text-xs text-slate-400 mt-1">Add extra recipients (comma or newline separated). These will receive the campaign in addition to your client list.</p>
                            </div>
                        </div>

                        {/* Image Upload - Touch Friendly */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Photo Attachment</label>
                            <div
                                className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#5C5CFF] hover:bg-indigo-50 transition group touch-manipulation active:scale-[0.98]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {form.image ? (
                                    <div className="relative">
                                        <img src={form.image} alt="Preview" className="max-h-40 mx-auto object-contain rounded-lg" />
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-sm font-bold opacity-0 group-hover:opacity-100 transition rounded-lg">
                                            Tap to Change
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3 py-2">
                                        <div className="w-14 h-14 mx-auto bg-slate-200 rounded-full flex items-center justify-center group-hover:bg-[#EBEBFF] transition">
                                            <Camera size={28} className="text-slate-400 group-hover:text-[#5C5CFF] transition" />
                                        </div>
                                        <div className="text-sm text-slate-500 font-medium">Tap to upload image</div>
                                        <div className="text-xs text-slate-400">Flash designs, tattoo photos, etc.</div>
                                    </div>
                                )}
                                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-slate-50 p-4 border-t border-slate-100 flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 bg-white border-2 border-slate-200 text-slate-500 py-3.5 rounded-xl font-bold text-sm hover:bg-slate-100 touch-manipulation"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleLaunchClick}
                            disabled={loading}
                            className="flex-1 bg-gradient-to-r from-[#5C5CFF] to-[#8E8EFF] text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-indigo-200 disabled:opacity-50 transition touch-manipulation flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    SENDING...
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    LAUNCH CAMPAIGN
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmation && (
                <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in-up">
                        <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle size={32} className="text-yellow-600" />
                        </div>
                        <h3 className="text-xl font-bold text-center text-slate-700 mb-2">Confirm Campaign Launch</h3>
                        <p className="text-center text-slate-500 mb-6">
                            You are about to send this campaign to <span className="font-bold text-[#5C5CFF]">{(audienceCount || 0) + savedLeads.length} recipient{((audienceCount || 0) + savedLeads.length) !== 1 ? 's' : ''}</span>.
                            {savedLeads.length > 0 && <><br /><span className="text-emerald-600 text-sm">({savedLeads.length} new leads will be saved)</span></>}
                            <br /><br />
                            This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="flex-1 bg-white border-2 border-slate-200 text-slate-500 py-3 rounded-xl font-bold text-sm hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmSend}
                                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-green-200"
                            >
                                ✓ Send Campaign
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Others / New Leads Popup Modal */}
            {showOthersPopup && (
                <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-fade-in-up">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-slate-700">Add New Leads</h3>
                                <p className="text-xs text-slate-400">These emails will be saved as new customers</p>
                            </div>
                            <button onClick={() => setShowOthersPopup(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-xs font-bold text-slate-500 mb-1.5">Enter Email Addresses</label>
                            <textarea
                                className="w-full border-2 border-slate-200 rounded-lg p-3 text-sm h-32 focus:border-emerald-500 outline-none resize-none font-mono"
                                placeholder="Enter one email per line:&#10;john@example.com&#10;jane@example.com&#10;lead@company.com"
                                value={othersEmails}
                                onChange={(e) => setOthersEmails(e.target.value)}
                            />
                            <p className="text-xs text-slate-400 mt-1">One email per line. These will be added to your client database.</p>
                        </div>

                        {savedLeads.length > 0 && (
                            <div className="mb-4 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                <p className="text-xs font-bold text-emerald-700 mb-2">✓ {savedLeads.length} leads ready to add:</p>
                                <div className="flex flex-wrap gap-1">
                                    {savedLeads.map((email, i) => (
                                        <span key={i} className="text-xs bg-white px-2 py-0.5 rounded-full border border-emerald-200 text-emerald-700">{email}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowOthersPopup(false); setForm({ ...form, audience_type: 'studio' }); }}
                                className="flex-1 bg-white border-2 border-slate-200 text-slate-500 py-3 rounded-xl font-bold text-sm hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    // Parse and validate emails
                                    const emails = othersEmails.split(/[\n,]+/)
                                        .map(e => e.trim())
                                        .filter(e => e && e.includes('@') && e.includes('.'));
                                    if (emails.length === 0) {
                                        toast.error('Please enter at least one valid email address');
                                        return;
                                    }
                                    setSavedLeads(emails);
                                    setForm({ ...form, custom_emails: emails.join('\n') });
                                    setShowOthersPopup(false);
                                }}
                                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 text-white py-3 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-emerald-200"
                            >
                                ✓ Add {othersEmails.split(/[\n,]+/).filter(e => e.trim() && e.includes('@')).length || 0} Leads
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const CreateConsentFormModal = ({ user, isSuperAdmin, onClose, onRefresh }) => {
    const [form, setForm] = useState({ title: '', description: '', agreements: [], health: [] });
    const [availableAgreements, setAvailableAgreements] = useState([]);
    const [availableHealth, setAvailableHealth] = useState([]);
    const [selectedAgreements, setSelectedAgreements] = useState([]);
    const [selectedHealth, setSelectedHealth] = useState([]);

    useEffect(() => {
        // Fetch available sections
        axios.get(`${API_URL}/agreements`).then(res => {
            const data = res.data;
            if (isSuperAdmin) setAvailableAgreements(data);
            else setAvailableAgreements(data.filter(a => a.studio_id === user.id));
        }).catch(console.error);

        axios.get(`${API_URL}/health`).then(res => {
            const data = res.data;
            if (isSuperAdmin) setAvailableHealth(data);
            else setAvailableHealth(data.filter(h => h.studio_id === user.id));
        }).catch(console.error);
    }, [isSuperAdmin, user.id]);

    const toggleAgreement = (id) => {
        if (selectedAgreements.includes(id)) setSelectedAgreements(selectedAgreements.filter(i => i !== id));
        else setSelectedAgreements([...selectedAgreements, id]);
    };

    const toggleHealth = (id) => {
        if (selectedHealth.includes(id)) setSelectedHealth(selectedHealth.filter(i => i !== id));
        else setSelectedHealth([...selectedHealth, id]);
    };

    const handleSave = async () => {
        const title = form.title?.trim();
        if (!title) { toast.error("Form Title is required."); return; }

        // Map IDs back to full content for saving (snapshotting)
        const finalAgreements = availableAgreements.filter(a => selectedAgreements.includes(a.id)).map(a => a.content);
        const finalHealth = availableHealth.filter(h => selectedHealth.includes(h.id)).map(h => h.questions);

        const payload = {
            title,
            description: form.description,
            agreements: finalAgreements,
            health_conditions: finalHealth,
            studio_id: user.id
        };

        try {
            const res = await axios.post(`${API_URL}/forms`, payload);
            if (res.data.success) {
                onRefresh();
                onClose();
            } else {
                toast.error(res.data.message || "Failed to save form");
            }
        } catch (error) {
                        toast.error(error.response?.data?.message || "Failed to save form. Check your connection.");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 w-full max-w-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-slate-700">Create New Consent Form</h2>
                    <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-red-500" /></button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Form Title</label>
                        <input className="w-full border border-slate-200 rounded p-3 text-sm" placeholder="e.g. Standard Tattoo Waiver" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-1">Description</label>
                        <textarea className="w-full border border-slate-200 rounded p-3 text-sm h-20" placeholder="Brief description for internal use..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>

                    {/* SELECT AGREEMENTS */}
                    <div>
                        <h3 className="font-bold text-slate-700 mb-2 border-b pb-1">Include Agreement Sections</h3>
                        <div className="space-y-2 max-h-40 overflow-y-auto border p-2 rounded bg-slate-50">
                            {availableAgreements.length === 0 && <div className="text-xs text-slate-400 italic">No agreement sections found. Add them in Settings.</div>}
                            {availableAgreements.map(a => (
                                <div key={a.id} className="flex items-start gap-2 p-2 bg-white border rounded cursor-pointer hover:border-blue-300" onClick={() => toggleAgreement(a.id)}>
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center mt-0.5 ${selectedAgreements.includes(a.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                                        {selectedAgreements.includes(a.id) && <Check size={12} />}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-slate-700">{a.title}</div>
                                        <div className="text-xs text-slate-500 line-clamp-1">{a.content}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* SELECT HEALTH SECTIONS */}
                    <div>
                        <h3 className="font-bold text-slate-700 mb-2 border-b pb-1">Include Health Questions</h3>
                        <div className="space-y-2 max-h-40 overflow-y-auto border p-2 rounded bg-slate-50">
                            {availableHealth.length === 0 && <div className="text-xs text-slate-400 italic">No health sections found. Add them in Health Sections.</div>}
                            {availableHealth.map(h => (
                                <div key={h.id} className="flex items-start gap-2 p-2 bg-white border rounded cursor-pointer hover:border-blue-300" onClick={() => toggleHealth(h.id)}>
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center mt-0.5 ${selectedHealth.includes(h.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                                        {selectedHealth.includes(h.id) && <Check size={12} />}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-slate-700">{h.title}</div>
                                        <div className="text-xs text-slate-500 line-clamp-1">{h.questions}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                    <button onClick={handleSave} className="order-1 sm:order-2 flex-1 bg-[#5C5CFF] text-white py-3 rounded font-bold text-sm hover:bg-[#4b4be6]">CREATE FORM</button>
                    <button onClick={onClose} className="order-2 sm:order-1 flex-1 bg-slate-100 text-slate-500 py-3 rounded font-bold text-sm hover:bg-slate-200">CANCEL</button>
                </div>
            </div>
        </div>
    );
};
